import React, { useState, useEffect, useMemo, useRef } from "react";
import { FaTimes, FaPaperclip, FaDownload, FaTrash } from "react-icons/fa";
import { FiLink, FiPlus } from 'react-icons/fi';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

const SaveIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
    <polyline points="17 21 17 13 7 13 7 21"></polyline>
    <polyline points="7 3 7 8 15 8"></polyline>
  </svg>
);
const AutofillIcon = ({ size = 16, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);
// SVG Icons (unchanged)
const CloseIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const MaximizeIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
  </svg>
);

const MinimizeIcon = ({ size = 20, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3"></path>
  </svg>
);

const PlusIcon = ({ size = 16, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const TrashIcon = ({ size = 16, className = "" }) => (
  <svg
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

// Success Icon Component
const SuccessIcon = ({ size = 48, className = '' }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const API_URL = "/api";

// Common units of measurement (unchanged)
const UOM_OPTIONS = [
  { value: "PCS", label: "Pieces" },
  { value: "KG", label: "Kilograms" },
  { value: "L", label: "Liters" },
  { value: "PACK", label: "Packs" },
];

// Status options (unchanged)
const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 0, label: "Inactive" },
];

// Inventory flag options - consistent with StockIn component
const INVENTORY_FLAG_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const initialHeader = {
  mrnDocumentId: null,
  mrnNumber: "",
  mrnDate: new Date().toISOString().split("T")[0],
  deliveryNoteNumber: "",
  challanNumber: "",
  vendorCode: "",
  vendorName: "",
  receivedBy: "",
  remarks: "",
  status: 1,
  pendingBillNumber: "",
  billDate: "",
  inventoryFlag: "yes",
  updateInventory: false,
  branchCode: "",
  department_id: "",
  locationCode: "",  
  invoiceReceivedDate: "", 
  vendorInvoiceNo: "",
   projectCode: "",
   attachment: "",
  attachmentFileName: ""
};

const initialItem = {
  mrnItemId: null,      // <--- ADD THIS
  itemCode: "",
  itemCode: "",
  productName: "",
  orderedQuantity: "",
  shortageQty: "",
  remainingQuantity: "",
  receivedQuantity: "",
  uom: "",
  acceptedQuantity: "",
  rejectedQuantity: "",
  warehouseCode: "",
  branchCode: "",
  attachment: "",
  attachmentFileName: "",
  remarks: "",
  tempId: "",
  purchaseOrderNumber: "",
  productCode: "",
  isFromPO: false,
  department_id: "",
  originalReceivedQuantity: 0,
  originalAcceptedQuantity: 0,
  availableReceivedQuantity: Infinity,
  availableAcceptedQuantity: Infinity,
  unitPrice: "",
  discount: "",
  taxCode: "",
  taxValue: "",
  total: "",
  // Product tracking fields
  productTrackingType: 0, // 0: None, 1: Batch, 2: Serial
   batches: [],
  expiryDate: "",
  serialNumbers: [],
  showBatchModal: false,
  showSerialModal: false,
   poLineNumber: "", 
   eWayBillNumber: "",
  vehicleNumber: "",
  transporter: "",
  grNumber: "",
   projectCode: ""
};


// PO Items Selection Modal (UPDATED with Keyboard Navigation)
const POItemsSelectionModal = ({
  isOpen,
  onClose,
  vendorCode,
  locationCode,
   branchCode,
   projectCode,
  onItemsSelected,
  existingItems = [],
}) => {

  const [poItems, setPOItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // State for search functionality
  const [searchTerm, setSearchTerm] = useState("");

  // NEW: State for keyboard navigation (highlighting)
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const tableBodyRef = useRef(null);

  useEffect(() => {
    if (isOpen && vendorCode && locationCode && branchCode) {
      loadPOItems();
    }
  }, [isOpen, vendorCode, locationCode, branchCode, projectCode]);

  // New Effect: Sync selected items with existing items in the form
  useEffect(() => {
    if (isOpen && poItems.length > 0) {
      const initiallySelected = poItems.filter((poItem) => {
        return existingItems.some((mrnItem) => {
          return (
            mrnItem.purchaseOrderNumber && 
            mrnItem.itemCode === poItem.ItemCode &&
            mrnItem.purchaseOrderNumber === poItem.PurchaseOrderNumber
          );
        });
      });
      
      setSelectedItems(initiallySelected);
    }
  }, [isOpen, poItems, existingItems]);

  // NEW: Global Keydown handler for Ctrl+S
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault(); 
        if (isOpen && selectedItems.length > 0) {
          handleAddSelectedItems();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, selectedItems]); 

  // NEW: Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && tableBodyRef.current && highlightedIndex >= 0) {
      const rows = tableBodyRef.current.querySelectorAll('tr');
      if (rows[highlightedIndex]) {
        rows[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Filter logic based on search term
  const filteredPoItems = useMemo(() => {
    if (!searchTerm) return poItems;
    const term = searchTerm.toLowerCase();
    return poItems.filter(item => {
      return (
        (item.PurchaseOrderNumber && item.PurchaseOrderNumber.toLowerCase().includes(term)) ||
        (item.ItemCode && item.ItemCode.toLowerCase().includes(term)) ||
        (item.ProductName && item.ProductName.toLowerCase().includes(term))
      );
    });
  }, [poItems, searchTerm]);

  const loadPOItems = async () => {
    try {
      setLoading(true);
      
      // Construct URL with projectCode query param
    let url = `${API_URL}/purchase-order/vendor/${vendorCode}/location/${locationCode}/branch/${branchCode}/items`;
    
    if (projectCode) {
      url += `?projectCode=${encodeURIComponent(projectCode)}`;
    }

    const headersResponse = await fetch(url, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
    });
      
      if (!headersResponse.ok) throw new Error("Failed to fetch PO Headers");
      
      const headersData = await headersResponse.json();
      
      const poNumbers = headersData.map(po => po.PurchaseOrderNumber).join(',');

      if (!poNumbers) {
        setPOItems([]);
        setLoading(false);
        return;
      }

      const filteredData = headersData.filter((item) => {
         const remainingQty = parseFloat(item.OutstandingQty); 
        return remainingQty > 0; 
      });
      
      setPOItems(filteredData);
      
    } catch (error) {
      // console.error("Error fetching PO items:", error);
      alert(`Error loading purchase order items: ${error.message}`);

      setPOItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItems((prev) => {
      const isSelected = prev.some(
        (selectedItem) =>
          selectedItem.ItemCode === item.ItemCode &&
          selectedItem.PurchaseOrderNumber === item.PurchaseOrderNumber
      );
      if (isSelected) {
        return prev.filter(
          (selectedItem) =>
            !(
              selectedItem.ItemCode === item.ItemCode &&
              selectedItem.PurchaseOrderNumber ===
              item.PurchaseOrderNumber 
            )
        );
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSelectAll = (e) => {
    // Apply select all to FILTERED items, not just all items
    if (e.target.checked) {
      setSelectedItems(filteredPoItems);
    } else {
      setSelectedItems([]);
    }
  };

  const handleAddSelectedItems = () => {
    onItemsSelected(selectedItems);
    onClose();
  };

  // UPDATED: Handle keyboard navigation
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (filteredPoItems.length > 0) {
        // Select the first result or highlighted result
        const indexToSelect = highlightedIndex >= 0 ? highlightedIndex : 0;
        handleItemSelect(filteredPoItems[indexToSelect]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (filteredPoItems.length > 0) {
        setHighlightedIndex(0);
        // Move focus to the table body
        setTimeout(() => {
          if (tableBodyRef.current) {
            tableBodyRef.current.focus();
          }
        }, 0);
      }
    }
  };

  // NEW: Handle navigation within the table
  const handleTableKeyDown = (e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredPoItems.length - 1) {
            return prev + 1;
          } else if (filteredPoItems.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredPoItems.length > 0) {
            return filteredPoItems.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case ' ':
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredPoItems.length) {
          handleItemSelect(filteredPoItems[highlightedIndex]);
        }
        break;
      case 'Escape':
        onClose();
        break;
      default:
        break;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center p-6 border-b">
          <h3 className="text-xl font-semibold text-[#384959]">
            Select Products from Purchase Orders
          </h3>
          <button
            className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
            onClick={onClose}
          >
            <CloseIcon size={24} />
          </button>
        </div>
        
        {/* Search Bar Area */}
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <input
            type="text"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6A89A7]"
            placeholder="Search by PO Number, Item Code, or Product Name... (Press Enter to select, Arrow Down to navigate list)"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setHighlightedIndex(-1); // Reset highlight when typing
            }}
            onKeyDown={handleSearchKeyDown}
          />
          <div className="text-xs text-gray-500 mt-1">
            Tip: Press <strong>Enter</strong> to select top result, <strong>Arrow Down</strong> to navigate list, <strong>Space</strong> to toggle. <strong>Ctrl+S</strong> to save.
          </div>
        </div>

        <div className="flex-grow overflow-y-auto p-6">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6A89A7] mx-auto"></div>
                <p className="mt-4 text-gray-600">
                  Loading purchase order items...
                </p>
              </div>
            </div>
          ) : filteredPoItems.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm ? "No items match your search." : "No purchase order items found for this vendor."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
             <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-[#BDDDFC] sticky top-0 z-10">
                <tr className="text-left text-sm font-semibold text-[#384959]">
                  <th className="p-3 w-12 text-center">
                    <input
                      type="checkbox"
                      onChange={handleSelectAll}
                      checked={
                        selectedItems.length === filteredPoItems.length &&
                        filteredPoItems.length > 0
                      }
                    />
                  </th>
                  <th className="p-3 w-32">PO Number</th>
                  <th className="p-3 w-32">Item Code</th>
                  <th className="p-3">Item Description</th>
                  <th className="p-3 w-20">UOM</th>
                   <th className="p-3 w-24 text-right">Rate</th> 
                  <th className="p-3 w-24 text-right">Ordered Qty</th>
                  <th className="p-3 w-28 text-right">Remaining Qty</th>
                  {/* <th className="p-3 w-20">UOM</th> */}
                  <th className="p-3 w-20">Location</th>
                  <th className="p-3 w-20">Branch</th>
                  <th className="p-3 w-32">Project Code</th>
                </tr>
              </thead>

              {/* UPDATED Tbody with ref and onKeyDown */}
              <tbody 
                ref={tableBodyRef}
                className="divide-y divide-gray-200 text-sm focus:outline-none"
                tabIndex={0}
                onKeyDown={handleTableKeyDown}
              >
                {filteredPoItems.map((item, index) => {
                  const isSelected = selectedItems.some(
                    (selectedItem) =>
                      selectedItem.ItemCode === item.ItemCode &&
                      selectedItem.PurchaseOrderNumber === item.PurchaseOrderNumber
                  );

                  return (
                    <tr
                      key={`${item.PurchaseOrderNumber}-${item.ItemCode}`}
                      className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                        isSelected ? "bg-blue-50" : ""
                      } ${
                        highlightedIndex === index ? "bg-blue-100" : ""
                      }`}
                      onClick={() => handleItemSelect(item)}
                    >
                      <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleItemSelect(item)}
                        />
                      </td>

                      <td className="p-3 font-medium">
                        {item.PurchaseOrderNumber}
                      </td>

                      <td className="p-3 font-semibold text-gray-800">
                        {item.ProductCode}
                      </td>

                      <td className="p-3 text-gray-700">
                        {item.ProductName}
                      </td>

                       <td className="p-3">
                        {item.UOM}
                      </td>
                      <td className="p-3 text-right font-medium text-gray-800">
                      {item.UnitPrice }
                    </td>

                      <td className="p-3 text-right">
                        {item.Quantity}
                      </td>

                      <td className="p-3 text-right font-medium text-blue-700" >
                        {item.OutstandingQty}
                      </td>

                      {/* <td className="p-3">
                        {item.UOM}
                      </td> */}

                      <td className="p-3">
                        {item.LocationCode}
                      </td>
                      <td className="p-3">
                        {item.BranchCode || '-'}
                      </td>
                      <td className="p-3">{item.ProjectCode || '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            </div>
          )}
        </div>
        <div className="flex justify-end p-6 border-t">
          <button
            className="px-4 py-2 border border-gray-300 rounded-lg mr-3 text-gray-700 hover:bg-gray-100"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] disabled:opacity-50"
            onClick={handleAddSelectedItems}
            disabled={selectedItems.length === 0}
          >
            Add Selected Items ({selectedItems.length})
          </button>
        </div>
      </div>
    </div>
  );
};
// Multi-Select Product Modal Component (unchanged)
const ProductMultiSelectModal = ({ 
  open, 
  onClose, 
  products, 
  onConfirm,
  onSaveToTable,
  onAutofill 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [isSaving, setIsSaving] = useState(false);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);
  const tableBodyRef = useRef(null);

  // Filter products based on search term
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(product => {
      return (
        (product.ProductCode && product.ProductCode.toLowerCase().includes(term)) || 
        (product.ProductName && product.ProductName.toLowerCase().includes(term)) ||
        (product.Status && product.Status.toLowerCase().includes(term)) ||
        (product.CategoryName && product.CategoryName.toLowerCase().includes(term)) ||
        (product.UOMName && product.UOMName.toLowerCase().includes(term)) ||
        (product.Description && product.Description.toLowerCase().includes(term)) ||
        (product.BranchName && product.BranchName.toLowerCase().includes(term)) ||
        (product.DepartmentName && product.DepartmentName.toLowerCase().includes(term)) ||
        (product.AllowProductNegativeInventory !== undefined && 
         (product.AllowProductNegativeInventory ? 'yes' : 'no').includes(term)) ||
        (product.AllowBatchOnProduct !== undefined && 
         (product.AllowBatchOnProduct ? 'yes' : 'no').includes(term)) ||
        (product.ProductType && product.ProductType.toLowerCase().includes(term)) ||
        (product.ProductionBOMType && product.ProductionBOMType.toLowerCase().includes(term))
      );
    });
  }, [products, searchTerm]);

  // Check if all filtered products are selected
  const allFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.every(product => selectedProducts.includes(product.ProductCode));
  }, [filteredProducts, selectedProducts]);

  // Check if some filtered products are selected (for indeterminate state)
  const someFilteredSelected = useMemo(() => {
    if (filteredProducts.length === 0) return false;
    return filteredProducts.some(product => selectedProducts.includes(product.ProductCode)) && !allFilteredSelected;
  }, [filteredProducts, selectedProducts, allFilteredSelected]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (open && tableBodyRef.current && highlightedIndex >= 0) {
      const items = tableBodyRef.current.querySelectorAll('.product-row');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, open]);

  // Reset and focus when modal opens
  useEffect(() => {
    if (open) {
      setSearchTerm('');
      setSelectedProducts([]);
      setHighlightedIndex(-1);
      
      // Focus search input after a short delay
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 100);
    }
  }, [open]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {

      // === Global Ctrl+S to trigger autofill ===
  if (e.ctrlKey && e.key.toLowerCase() === 's') {
    e.preventDefault(); // Prevent browser's default "Save Page" behavior
    handleAutofill();
    return;
  }

    // Handle search input keys
    if (e.target === searchInputRef.current) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(0);
        // Move focus to the table body
        setTimeout(() => {
          if (tableBodyRef.current) {
            tableBodyRef.current.focus();
          }
        }, 0);
        return;
      }
      
      // Handle Enter and Ctrl+Enter in search input
      if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredProducts.length > 0) {
          if (e.ctrlKey) {
            
          } else {
            // Regular Enter for selecting first product
            const firstProduct = filteredProducts[0];
            toggleProductSelection(firstProduct.ProductCode);
            // IMPORTANT: Keep focus in search input so user can continue searching
            searchInputRef.current.focus();
          }
        }
      }
      return;
    }

    // Handle table body navigation (rest of the existing code remains the same)
    if (e.target === tableBodyRef.current) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex(prev => {
            if (prev < filteredProducts.length - 1) {
              return prev + 1;
            } else if (filteredProducts.length > 0) {
              return 0; // Loop back to first item
            }
            return prev;
          });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex(prev => {
            if (prev > 0) {
              return prev - 1;
            } else if (filteredProducts.length > 0) {
              return filteredProducts.length - 1; // Loop to last item
            }
            return prev;
          });
          break;
        case ' ':
          e.preventDefault();
          if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
            toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
          }
          break;
        case 'Enter':
          e.preventDefault();
          if (e.ctrlKey) {
            // Ctrl+Enter for autofill
            handleAutofill();
          } else if (highlightedIndex >= 0 && highlightedIndex < filteredProducts.length) {
            // Regular Enter for selecting highlighted product
            toggleProductSelection(filteredProducts[highlightedIndex].ProductCode);
          }
          break;
        case 'Escape':
          onClose();
          break;
        default:
          break;
      }
    }
  };
  
  // Toggle product selection
  const toggleProductSelection = (productCode) => {
    setSelectedProducts(prev => 
      prev.includes(productCode)
        ? prev.filter(code => code !== productCode)
        : [...prev, productCode]
    );
  };

  // Handle select all filtered products
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // Add all filtered products to selection
      const allProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => [...new Set([...prev, ...allProductCodes])]);
    } else {
      // Remove all filtered products from selection
      const filteredProductCodes = filteredProducts.map(p => p.ProductCode);
      setSelectedProducts(prev => prev.filter(code => !filteredProductCodes.includes(code)));
    }
  };

  // Handle clear all selection
  const handleClearAll = () => {
    setSelectedProducts([]);
  };

  // Handle confirm selection
  const handleConfirm = () => {
    onConfirm(selectedProducts);
    onClose();
  };

  // Handle save to table
  const handleSaveToTable = async () => {
    if (selectedProducts.length === 0) return;
    
    setIsSaving(true);
    try {
      await onSaveToTable(selectedProducts);
      alert('Items saved to table successfully!');
    } catch (error) {
      // console.error('Error saving to table:', error);
      alert('Failed to save items to table');
    } finally {
      setIsSaving(false);
    }
  };
  
  // In ProductMultiSelectModal component
  const handleAutofill = () => {
    if (selectedProducts.length === 0) return;
    
    try {
      // Make sure we're passing the actual product objects, not just codes
      const selectedProductObjects = selectedProducts.map(productCode => 
        products.find(p => p.ProductCode === productCode)
      ).filter(Boolean); // Filter out any undefined values
      
      if (selectedProductObjects.length === 0) {
        alert('No valid products selected');
        return;
      }
      
      const result = onAutofill(selectedProductObjects);
      
      if (result && result.success) {
        // Success - modal will close in finally block
      } else {
        alert(result?.error || 'Error autofilling table. Please try again.');
      }
    } catch (error) {
      // console.error('Error during autofill:', error);
      alert('Error autofilling table. Please try again.');
    } finally {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full  h-[100vh] flex flex-col border border-gray-200">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-800">Select Multiple Products</h3>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-gray-200"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        <div className="p-5 border-b border-gray-200 bg-white">
          <div className="relative">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by Product Code, Name, Status, Category, UOM, Description, Branch, Department, etc..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base shadow-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            <div className="absolute right-3 top-3 text-gray-400">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="mt-3 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-100">
            <div className="font-medium text-blue-800 mb-1">Keyboard Shortcuts:</div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↑</kbd>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">↓</kbd>
                <span className="ml-1">Navigate</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Space</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Enter</kbd>
                <span className="ml-1">Select</span>
              </span>
              <span className="inline-flex items-center px-2 py-1 bg-white rounded-md text-xs font-medium text-gray-700 border border-gray-200">
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">Ctrl</kbd>
                <span className="mx-1">+</span>
                <kbd className="px-1 py-0.5 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">S</kbd>
                <span className="ml-1">Autofill</span>
              </span>
            </div>
          </div>
        </div>
        
        <div 
          ref={listRef}
          className="flex-grow overflow-y-auto bg-gray-50 relative"
        >
          {filteredProducts.length > 0 ? (
            <div className="h-full flex flex-col">
              {/* Single Table with Sticky Header */}
              <div className="flex-grow overflow-y-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  {/* Sticky Header */}
                  <thead className="sticky top-0 z-30 bg-gray-100 shadow-md">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-12">
                        <input
                          type="checkbox"
                          className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                          checked={allFilteredSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someFilteredSelected;
                            }
                          }}
                          onChange={handleSelectAll}
                          title={allFilteredSelected ? "Deselect all" : someFilteredSelected ? "Select all filtered" : "Select all filtered"}
                        />
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Product Code
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[200px]">
                        Product Name
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Category
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-20">
                        UOM
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[180px]">
                        Description
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Branch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Department
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                        Negative Inv.
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                        Batch
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        Product Type
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-28">
                        BOM Type
                      </th>
                    </tr>
                  </thead>
                  
                  {/* Scrollable Body */}
                  <tbody 
                    ref={tableBodyRef}
                    className="bg-white divide-y divide-gray-200 focus:outline-none"
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                  >
                    {filteredProducts.map((product, index) => (
                      <tr
                        key={product.ProductCode}
                        className={`product-row hover:bg-blue-50 transition-colors ${
                          highlightedIndex === index ? 'bg-blue-100' : ''
                        } ${selectedProducts.includes(product.ProductCode) ? 'bg-blue-50' : ''}`}
                        onClick={() => toggleProductSelection(product.ProductCode)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                            checked={selectedProducts.includes(product.ProductCode)}
                            onChange={() => {}}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 w-32">
                          {product.ProductCode}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium min-w-[200px]">
                          {product.ProductName}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            product.Status === 'Active' ? 'bg-green-100 text-green-800' : 
                            product.Status === 'Inactive' ? 'bg-red-100 text-red-800' : 
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {product.Status || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.CategoryName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-20">
                          {product.UOMName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate min-w-[180px]" title={product.Description || ''}>
                          {product.Description || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.BranchName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.DepartmentName || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-32">
                          {product.AllowProductNegativeInventory !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowProductNegativeInventory ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowProductNegativeInventory ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm w-24">
                          {product.AllowBatchOnProduct !== undefined ? (
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              product.AllowBatchOnProduct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {product.AllowBatchOnProduct ? 'Yes' : 'No'}
                            </span>
                          ) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductType || 'N/A'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 w-28">
                          {product.ProductionBOMType || 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full py-16">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="mt-4 text-lg font-medium text-gray-900">No products found</h3>
              <p className="mt-2 text-sm text-gray-500">
                {products.length === 0 
                  ? 'No products are available in the system.' 
                  : 'No products match your search criteria. Try different keywords.'}
              </p>
            </div>
          )}
        </div>
        
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="text-sm text-gray-700">
              <span className="font-medium">{selectedProducts.length}</span> product{selectedProducts.length !== 1 ? 's' : ''} selected
            </div>
            {selectedProducts.length > 0 && (
              <button
                type="button"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                onClick={handleClearAll}
              >
                Clear All
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleSaveToTable}
              disabled={selectedProducts.length === 0 || isSaving}
            >
              <SaveIcon className="mr-2 h-4 w-4" />
              {isSaving ? 'Saving...' : 'Save to Table'}
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={handleAutofill}
              disabled={selectedProducts.length === 0}
            >
              <AutofillIcon className="mr-2 h-4 w-4" />
              Autofill Table
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              onClick={handleConfirm}
              disabled={selectedProducts.length === 0}
            >
              Add Selected
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Reusable Searchable Dropdown Component with Auto-Focus on Search (unchanged)
const SearchableDropdown = ({ 
  options, 
  value, 
  onChange, 
  placeholder = "Select",
  error = false,
  width = "w-full",
  getOptionLabel = (option) => option.name || option.label || option.toString(),
  getOptionValue = (option) => option.value || option.id || option.toString(),
  getOptionSecondaryLabel = (option) => option.code || option.id || "",
  disabled = false,
  className = "",
  onMultiSelectTrigger = null,
  multiSelectShortcut = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const dropdownListRef = useRef(null);
  const searchInputRef = useRef(null);

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width
      });
    }
  }, [isOpen]);

  // Handle window resize and scroll to update position
  useEffect(() => {
    if (!isOpen) return;
    
    const handleUpdatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
          top: rect.bottom,
          left: rect.left,
          width: rect.width
        });
      }
    };
    
    window.addEventListener('resize', handleUpdatePosition);
    window.addEventListener('scroll', handleUpdatePosition);
    
    return () => {
      window.removeEventListener('resize', handleUpdatePosition);
      window.removeEventListener('scroll', handleUpdatePosition);
    };
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && dropdownListRef.current && highlightedIndex >= 0) {
      const items = dropdownListRef.current.querySelectorAll('.dropdown-item');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      // Clear search term and reset highlighted index when opening
      setSearchTerm('');
      setHighlightedIndex(-1);
      
      // Focus the search input after a short delay to ensure it's rendered
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 0);
    }
  }, [isOpen]);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm) return options;
    const term = searchTerm.toLowerCase();
    
    return options.filter(option => {
      const label = (getOptionLabel(option) || '').toString().toLowerCase();
      const secondaryLabel = (getOptionSecondaryLabel(option) || '').toString().toLowerCase();
      
      return label.includes(term) || secondaryLabel.includes(term);
    });
  }, [options, searchTerm, getOptionLabel, getOptionSecondaryLabel]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Check for multi-select shortcut
    if (onMultiSelectTrigger && multiSelectShortcut && e.ctrlKey && e.key === multiSelectShortcut) {
      e.preventDefault();
      onMultiSelectTrigger();
      return;
    }

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev < filteredOptions.length - 1) {
            return prev + 1;
          } else if (filteredOptions.length > 0) {
            return 0; // Loop back to first item
          }
          return prev;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => {
          if (prev > 0) {
            return prev - 1;
          } else if (filteredOptions.length > 0) {
            return filteredOptions.length - 1; // Loop to last item
          }
          return prev;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
          handleSelectOption(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  const handleSelectOption = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div
        className={`${width} border rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center ${
          error ? 'border-red-400' : 'border-[#E2E8F0]'
        } ${isOpen ? 'ring-1 ring-[#BDDDFC]' : ''} ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        ref={triggerRef}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="truncate">
          {selectedOption ? getOptionLabel(selectedOption) : placeholder}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      
      {isOpen && (
        <div 
          className="fixed z-[9999] bg-white border border-[#E2E8F0] rounded-lg shadow-lg"
          style={{
            top: `${position.top}px`,
            left: `${position.left}px`,
            width: `${position.width}px`,
          }}
        >
          <div className="p-2 border-b border-[#E2E8F0]">
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search..."
              className="w-full px-2 py-1 border border-[#E2E8F0] rounded focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          
          <div 
            className="max-h-60 overflow-y-auto" 
            ref={dropdownListRef}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  className={`dropdown-item px-3 py-2 cursor-pointer hover:bg-[#F1F5F9] ${
                    highlightedIndex === index ? 'bg-[#BDDDFC]' : ''
                  } ${value === getOptionValue(option) ? 'bg-[#E6F0FF]' : ''}`}
                  onClick={() => handleSelectOption(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <div className="font-medium">{getOptionLabel(option)}</div>
                  {getOptionSecondaryLabel(option) && (
                    <div className="text-xs text-gray-500">{getOptionSecondaryLabel(option)}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-gray-500">No options found</div>
            )}
          </div>
        </div>
      )}
      
      {error && <span className="text-xs text-red-500">*</span>}
    </div>
  );
};

// Batch Modal Component
const BatchTableModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialBatches = [], 
  productName = "", 
  totalQuantity = 0,
  acceptedQuantity = 0 
}) => {
  const [batches, setBatches] = useState([
    { batchNumber: "", expiryDate: "", quantity: "" }
  ]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (initialBatches.length > 0) {
        // Format the expiry dates to YYYY-MM-DD format for the input[type="date"]
        const formattedBatches = initialBatches.map(batch => ({
          ...batch,
          expiryDate: batch.expiryDate ? new Date(batch.expiryDate).toISOString().split('T')[0] : ""
        }));
        setBatches(formattedBatches);
      } else {
        setBatches([{ batchNumber: "", expiryDate: "", quantity: "" }]);
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, initialBatches]);

  // Calculate remaining quantity
  const remainingQuantity = acceptedQuantity - batches.reduce(
    (sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0
  );

  // Handle batch field changes
  const handleBatchChange = (index, field, value) => {
    const newBatches = [...batches];
    newBatches[index][field] = value;
    
    // Clear errors for this field
    if (errors[`${field}_${index}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${field}_${index}`];
      setErrors(newErrors);
    }
    
    // Auto-generate new row if quantity is entered and it's less than remaining
    if (field === 'quantity' && value && index === batches.length - 1) {
      const enteredQty = parseFloat(value) || 0;
      const currentTotal = batches.reduce((sum, batch, i) => 
        i === index ? sum + enteredQty : sum + (parseFloat(batch.quantity) || 0), 0);
      
      // If there's still remaining quantity after this entry, add a new row
      if (currentTotal < acceptedQuantity) {
        // Add a new empty row
        newBatches.push({ batchNumber: "", expiryDate: "", quantity: "" });
      }
    }
    
    setBatches(newBatches);
  };

  // Remove a batch row
  const removeBatchRow = (index) => {
    if (batches.length > 1) {
      const newBatches = [...batches];
      newBatches.splice(index, 1);
      setBatches(newBatches);
    }
  };

  // Auto-fill quantity to cover remaining
  const autoFillQuantity = (index) => {
    const newBatches = [...batches];
    newBatches[index].quantity = remainingQuantity.toString();
    
    // Remove any extra empty rows if we've covered the full quantity
    if (remainingQuantity > 0) {
      // Keep only this row if it covers the full remaining quantity
      setBatches([newBatches[index]]);
    } else {
      setBatches(newBatches);
    }
  };

  // Validate all batches
  const validateBatches = () => {
    const newErrors = {};
    let isValid = true;

    // Check if we have at least one batch
    if (batches.length === 0) {
      newErrors.general = "At least one batch is required";
      isValid = false;
    }

    batches.forEach((batch, index) => {
      // Validate batch number
      if (!batch.batchNumber || batch.batchNumber.trim() === '') {
        newErrors[`batchNumber_${index}`] = "Batch number is required";
        isValid = false;
      }

      // Validate expiry date
      if (!batch.expiryDate) {
        newErrors[`expiryDate_${index}`] = "Expiry date is required";
        isValid = false;
      }

      // Validate quantity
      if (!batch.quantity || batch.quantity.trim() === '' || parseFloat(batch.quantity) <= 0) {
        newErrors[`quantity_${index}`] = "Quantity must be greater than 0";
        isValid = false;
      }
    });

    // Check if total batch quantity equals accepted quantity
    const totalBatchQty = batches.reduce(
      (sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0
    );
    
    if (Math.abs(totalBatchQty - acceptedQuantity) > 0.001) {
      newErrors.total = `Total batch quantity (${totalBatchQty.toFixed(6)}) must equal accepted quantity (${acceptedQuantity})`;
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSave = async () => {
  setIsSubmitting(true);
  
  try {
    if (validateBatches()) {
      // Filter out any incomplete batches (only include batches with all required fields)
      const validBatches = batches.filter(batch => 
        batch.batchNumber && 
        batch.batchNumber.trim() !== '' && 
        batch.expiryDate && 
        batch.quantity && 
        parseFloat(batch.quantity) > 0
      );
      
      // Format batches for backend
      const formattedBatches = validBatches.map(batch => ({
        batchNumber: batch.batchNumber.trim(),
        expiryDate: batch.expiryDate,
        quantity: batch.quantity.toString()
      }));
      
      await onSave(formattedBatches);
      onClose();
    }
  } catch (error) {
    // console.error('Error saving batches:', error);
    setErrors({
      ...errors,
      general: error.message || "Failed to save batches"
    });
  } finally {
    setIsSubmitting(false);
  }
};
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl p-0 max-h-[90vh] flex flex-col border border-gray-300">
        {/* Header */}
        <div className="bg-slate-200 px-6 py-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-black">Batch Details</h3>
            <button 
              onClick={onClose} 
              className="text-gray-900 hover:text-black transition-colors"
            >
              <CloseIcon size={24} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-slate-100 px-6 py-3 border-b border-slate-200">
          <div className="flex items-center space-x-8">
            <div className="flex items-center">
              <span className="text-sm font-medium text-slate-600 mr-2">Product:</span>
              <span className="text-sm font-semibold text-slate-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm font-medium text-slate-600 mr-2">Total Quantity:</span>
              <span className="text-sm font-semibold text-slate-800">{acceptedQuantity}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm font-medium text-slate-600 mr-2">Remaining:</span>
              <span className={`text-sm font-semibold ${remainingQuantity > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {remainingQuantity.toFixed(6)}
              </span>
            </div>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
          <p className="text-sm text-blue-700">
            <span className="font-medium">Instructions:</span> Enter batch details for each batch. 
            New rows will be automatically added when you enter a quantity less than the total required. 
            Use the "Fill" button to automatically fill a row with the remaining quantity.
          </p>
        </div>
        
        {/* Error Messages */}
        {(errors.general || errors.total) && (
          <div className="mx-6 mt-4 space-y-2">
            {errors.general && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{errors.general}</p>
              </div>
            )}
            {errors.total && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 text-sm">{errors.total}</p>
              </div>
            )}
          </div>
        )}
        
        {/* Batch Table */}
        <div className="flex-grow overflow-hidden flex flex-col p-6">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100 sticky top-0 z-10 shadow-sm">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-12">#</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Batch Number <span className='text-red-600'>*</span></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Expiry Date <span className='text-red-600'>*</span></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-32">Quantity <span className='text-red-600'>*</span></th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {batches.map((batch, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50 hover:bg-slate-100'}>
                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-slate-900">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                     <input
  type="text"
  value={batch.batchNumber}
  onChange={(e) => handleBatchChange(index, 'batchNumber', e.target.value.toUpperCase())}
  className={`w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 uppercase text-sm ${errors[`batchNumber_${index}`] ? 'border-red-500' : ''}`}
  placeholder="Enter batch number"
/>
                      {errors[`batchNumber_${index}`] && (
                        <p className="text-red-500 text-xs mt-1">{errors[`batchNumber_${index}`]}</p>
                      )}
                    </td>
                     <td className="px-4 py-3 whitespace-nowrap">
    <LocalizationProvider dateAdapter={AdapterDayjs}>
  <DatePicker
    value={batch.expiryDate ? dayjs(batch.expiryDate) : null}
    onChange={(newValue) => handleBatchChange(index, 'expiryDate', newValue ? newValue.format('YYYY-MM-DD') : '')}
    format="DD/MM/YYYY"
    slotProps={{
      textField: {
        size: 'small',
        className: 'w-full',
        error: !!errors[`expiryDate_${index}`]
      }
    }}
  />
</LocalizationProvider>
    {errors[`expiryDate_${index}`] && (
      <p className="text-red-500 text-xs mt-1">{errors[`expiryDate_${index}`]}</p>
    )}
  </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="0"
                          inputMode="decimal"  step="0.000001"
                          value={batch.quantity}
                          onChange={(e) => handleBatchChange(index, 'quantity', e.target.value)}
                          className={`w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-sm ${errors[`quantity_${index}`] ? 'border-red-500' : ''}`}
                          placeholder="0.00"
                        />
                        <button
                          type="button"
                          onClick={() => autoFillQuantity(index)}
                          className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-xs hover:bg-blue-200"
                          title="Auto-fill with remaining quantity"
                        >
                          Fill
                        </button>
                      </div>
                      {errors[`quantity_${index}`] && (
                        <p className="text-red-500 text-xs mt-1">{errors[`quantity_${index}`]}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex space-x-1">
                        <button
                          type="button"
                          onClick={() => removeBatchRow(index)}
                          disabled={batches.length <= 1}
                          className={`p-1 rounded-md ${batches.length <= 1 ? 'text-gray-300 cursor-not-allowed' : 'text-red-600 hover:bg-red-100'}`}
                          title="Remove batch"
                        >
                          <TrashIcon size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="text-sm text-slate-600">
              Total batches: <span className="font-medium">{batches.length}</span> | 
              Remaining quantity: <span className={`font-medium ${remainingQuantity > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {remainingQuantity.toFixed(6)}
              </span>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                className="px-4 py-2 border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-colors disabled:opacity-50"
                onClick={handleSave}
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Saving...' : 'Save Batch Details'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Serial Modal Component
const SerialModal = ({ isOpen, onClose, onSave, count = 0, initialSerialNumbers = [], productName = "", locationCode = "" }) => {
  const [serialNumbers, setSerialNumbers] = useState(
    Array.from({ length: count }, (_, i) => initialSerialNumbers[i] || "")
  );
  const inputRefs = useRef([]);

 useEffect(() => {
    if (isOpen) {
      if (initialSerialNumbers.length > 0) {
        setSerialNumbers(initialSerialNumbers);
      } else {
        setSerialNumbers(Array.from({ length: count }, () => ""));
      }
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  }, [isOpen, count, initialSerialNumbers]);

  const handleSerialChange = (index, value) => {
    const newSerialNumbers = [...serialNumbers];
    newSerialNumbers[index] = value;
    setSerialNumbers(newSerialNumbers);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      if (index < count - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleSave = () => {
    if (serialNumbers.every(sn => sn.trim() !== "")) {
      onSave(serialNumbers.map(sn => sn.trim()));
      onClose();
    } else {
      alert("All serial numbers are required");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl p-0 max-h-[90vh] flex flex-col border border-gray-300">
        {/* Header */}
        <div className="bg-slate-200 px-4 py-2 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-bold text-black">Serial Number Entry</h3>
            <button 
              onClick={onClose} 
              className="text-gray-900 hover:text-black transition-colors"
            >
              <CloseIcon size={20} />
            </button>
          </div>
        </div>
        
        {/* Product Info Bar */}
        <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
          <div className="flex items-center space-x-6">
            <div className="flex items-center">
              <span className="text-xs font-medium text-slate-600 mr-1">Product:</span>
              <span className="text-xs font-semibold text-slate-800">{productName || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-slate-600 mr-1">Location:</span>
              <span className="text-xs font-semibold text-slate-800">{locationCode || "N/A"}</span>
            </div>
            <div className="flex items-center">
              <span className="text-xs font-medium text-slate-600 mr-1">Quantity:</span>
              <span className="text-xs font-semibold text-slate-800">{count}</span>
            </div>
          </div>
        </div>
        
        {/* Instructions */}
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
          <p className="text-xs text-slate-700">
            Enter {count} unique serial number{count !== 1 ? 's' : ''}. Press Enter or Tab to move to the next field.
          </p>
        </div>
        
        {/* Serial Numbers Table */}
        <div className="flex-grow overflow-hidden flex flex-col">
          <div className="overflow-y-auto flex-grow">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-10">#</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Serial Number</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Product</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider">Location</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700 uppercase tracking-wider w-10">Qty</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {Array.from({ length: count }).map((_, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50 hover:bg-slate-100'}>
                    <td className="px-3 py-2 whitespace-nowrap text-xs font-medium text-slate-900">
                      {index + 1}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <input
                        ref={el => inputRefs.current[index] = el}
                        type="text"
                        value={serialNumbers[index]}
                        onChange={(e) => handleSerialChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        className="w-full border border-slate-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 uppercase text-xs"
                        placeholder={`Serial ${index + 1}`}
                      />
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-900">
                      {productName || "N/A"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">
                      {locationCode || "N/A"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600 text-center">
                      1
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer */}
        <div className="bg-slate-50 px-4 py-2 border-t border-slate-200 rounded-b-lg">
          <div className="flex justify-between items-center">
            <div className="text-xs text-slate-600">
              {serialNumbers.filter(sn => sn.trim() !== "").length} of {count} serial numbers entered
            </div>
            <div className="flex space-x-2">
              <button
                className="px-3 py-1 border border-slate-300 rounded text-xs text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-1 focus:ring-slate-500 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 border border-transparent rounded text-xs text-white bg-slate-700 hover:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-slate-500 transition-colors"
                onClick={handleSave}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MaterialReceiptNoteFormModal = ({
  isOpen,
  onClose,
  mode = "create",
  initialData = {},
  onSubmit,
  onFormSuccess
}) => {
  const [header, setHeader] = useState(initialHeader);
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dropdownData, setDropdownData] = useState({
    vendors: [],
    locations: [],
    branches: [],
    projects: [],
    nextMRNNumber: "",
  });
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [showPOItemsModal, setShowPOItemsModal] = useState(false);
  const fileInputRefs = useRef([]);
  
  // Added state for user management
  const [currentUser, setCurrentUser] = useState(null);
  const [isUserValid, setIsUserValid] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [productItems, setProductItems] = useState([]);
  
  const [toast, setToast] = useState({
    open: false,
    type: 'error',
    title: '',
    message: ''
  });

   const headerFileInputRef = useRef(null);

     const [columnWidths, setColumnWidths] = useState({
    rowNum: 40,
    productCode: 200,
    productName: 280,
    uom: 80,
    rate: 150,
    balanceQty: 200,
    receivedQty: 200,
    shortExcess: 200,
    location: 200,
    branch: 200,
    projectCode: 200,
    department: 200, // Conditional
    trackingType: 200,
    batchSerial: 200,
    poNumber: 200,
    poLine: 200,
    attachment: 200,
    remarks: 280,
    action: 60
  });

  const resizingRef = useRef(null);

  // Department-related state
  const [departments, setDepartments] = useState([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [screenConfig, setScreenConfig] = useState(null);
  const [showDepartmentField, setShowDepartmentField] = useState(false);
  const [showDepartmentItemField, setShowDepartmentItemField] = useState(false);
  
  // Tax codes state
  const [taxCodes, setTaxCodes] = useState([]);

  // Product tracking state
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [currentBatchItem, setCurrentBatchItem] = useState(null);
  const [showSerialModal, setShowSerialModal] = useState(false);
  const [currentSerialItem, setCurrentSerialItem] = useState(null);

  // Add these state variables
  const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
  const [products, setProducts] = useState([]); // For storing product list
  
  // Helper function to calculate item total
  const calculateItemTotal = (item) => {
    const unitPrice = parseFloat(item.unitPrice) || 0;
    const discount = parseFloat(item.discount) || 0;
    const taxCode = item.taxCode;
    const acceptedQty = parseFloat(item.acceptedQuantity) || 0;
    
    // Find tax rate
    const taxRate = taxCodes.find(tc => tc.TaxCode === taxCode)?.Rate || 0;
    
    // Calculate taxable amount per unit (ensure it's not negative)
    const taxableAmountPerUnit = Math.max(0, unitPrice - discount);
    
    // Calculate tax value per unit
    const taxValuePerUnit = taxableAmountPerUnit * (taxRate / 100);
    
    // Calculate total per unit (including tax)
    const totalPerUnit = taxableAmountPerUnit + taxValuePerUnit;
    
    // Calculate total for the entire accepted quantity
    const total = totalPerUnit * acceptedQty;
    
    return {
      taxValue: isNaN(taxValuePerUnit) ? "0.00" : taxValuePerUnit.toFixed(6),
      total: isNaN(total) ? "0.00" : total.toFixed(6)
    };
  };
  
  // Function to fetch current user
  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsUserValid(false);
        setAuthError('No token found. Please log in again.');
        return false;
      }
      const userRes = await fetch(`${API_URL}/current-user`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const userData = await userRes.json();
      
      if (userData.user && userData.user.UserID) {
        setCurrentUser(userData.user);
        setIsUserValid(true);
        setAuthError(null);
        // console.log('Current user fetched:', userData);
        return true;
      } else {
        // console.error('Invalid user data received:', userData);
        setIsUserValid(false);
        setAuthError('User information not available. Please log in again.');
        return false;
      }
    } catch (error) {
      // console.error('Error fetching current user:', error);
      setIsUserValid(false);
      setAuthError('Error fetching user information. Please refresh the page.');
      return false;
    }
  };
  
  // Function to fetch departments
  const fetchDepartments = async () => {
    setLoadingDepartments(true);
    try {
      const response = await fetch(`${API_URL}/departments`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      const data = await response.json();
      // console.log('Fetched departments:', data);
      setDepartments(data);
    } catch (error) {
      // console.error('Error fetching departments:', error);
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };
const handlePOItemsSelected = (selectedItems) => {
  // 1. Map the selected PO items to the MRN format
  const newItems = selectedItems.map(item => {
    
    const masterProduct = productItems.find(p => p.ProductCode === item.ProductCode);
    // FIX: Use the same robust fallback logic
    const trackingType = masterProduct ? (masterProduct.productTrackingType ?? masterProduct.ProductTrackingType ?? masterProduct.AllowProductTracking) : 0;

    return {
      ...initialItem,
      tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, 
      purchaseOrderNumber: item.PurchaseOrderNumber,
      itemCode: item.ItemCode, // Line Number
       poLineNumber: item.LineNumber ,
      productCode: item.ProductCode, // Product Code
      productName: item.ProductName,
       projectCode: item.ProjectCode || "",
      
      orderedQuantity: item.OrderedQty?.toString() || "0", 
      remainingQuantity: item.OutstandingQty?.toString() || "0",
      uom: item.UOM,
      warehouseCode: item.LocationCode, 
       branchCode: item.BranchCode || "", 
       shortageQty: "0",
      
      isFromPO: true,
      department_id: "",
      originalReceivedQuantity: 0,
      originalAcceptedQuantity: 0,
      
      availableReceivedQuantity: parseFloat(item.OutstandingQty || 0),
      availableAcceptedQuantity: parseFloat(item.OutstandingQty || 0),
      
      unitPrice: item.UnitPrice?.toString() || "0",
      discount: item.Discount?.toString() || "0",
      taxCode: item.TaxCode || "",
      taxValue: "0.00",
      total: "0.00",
      
      productTrackingType: trackingType,
      batchNumber: "",
      expiryDate: "",
      serialNumbers: [],
      batches: [],
    };
  });

  // 2. Get a list of unique identifiers for items currently in the table
  // We use a combination of itemCode and purchaseOrderNumber to uniquely identify a row
  const existingItemKeys = items.map(
    (item) => `${item.itemCode}-${item.purchaseOrderNumber}`
  );

  // 3. Filter out any items from 'newItems' that already exist in 'items'
  const uniqueNewItems = newItems.filter(
    (newItem) =>
      !existingItemKeys.includes(
        `${newItem.itemCode}-${newItem.purchaseOrderNumber}`
      )
  );

  // 4. Only update state if there are actually new items to add
  if (uniqueNewItems.length > 0) {
    setItems(prev => [...prev, ...uniqueNewItems]);
  }
};
  // Function to fetch screen configuration
  const fetchScreenConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/screens`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      const data = await response.json();
      // console.log('Fetched screen configurations:', data);
      
      // Find the MRN screen configuration
      const mrnScreen = data.screens.find(screen => 
        screen.ScreenName.toLowerCase() === 'MRN'.toLowerCase()
      );
      
      // console.log('MRN screen configuration:', mrnScreen);
      if (mrnScreen) {
        setScreenConfig(mrnScreen);
        // Show department field if ShowOnHeader is true
        const shouldShowDepartment = mrnScreen.ShowOnHeader === true;
        setShowDepartmentField(shouldShowDepartment);
        
        // Show department item field if ShowOnItem is true
        const shouldShowDepartmentItem = mrnScreen.ShowOnItem === true;
        setShowDepartmentItemField(shouldShowDepartmentItem);
        
        // If department fields should be shown, fetch departments
        if (shouldShowDepartment || shouldShowDepartmentItem) {
          fetchDepartments();
        }
      }
    } catch (error) {
      // console.error('Error fetching screen configuration:', error);
      setShowDepartmentField(false);
      setShowDepartmentItemField(false);
    }
  };
  
  // Calculate summary values
  const summary = useMemo(() => {
    let totalItems = items.length;
    let totalReceivedQuantity = 0;
    let totalAcceptedQuantity = 0;
    let totalRejectedQuantity = 0;
    let totalAmount = 0;
    
    items.forEach((item) => {
      const receivedQty = parseFloat(item.receivedQuantity) || 0;
      const acceptedQty = parseFloat(item.acceptedQuantity) || 0;
      const rejectedQty = parseFloat(item.rejectedQuantity) || 0;
      const itemTotal = parseFloat(item.total) || 0;
      
      totalReceivedQuantity += receivedQty;
      totalAcceptedQuantity += acceptedQty;
      totalRejectedQuantity += rejectedQty;
      totalAmount += itemTotal;
    });
    
    return {
      totalItems,
      totalReceivedQuantity: totalReceivedQuantity.toFixed(6),
      totalAcceptedQuantity: totalAcceptedQuantity.toFixed(6),
      totalRejectedQuantity: totalRejectedQuantity.toFixed(6),
      totalAmount: totalAmount.toFixed(6),
    };
  }, [items]);

  useEffect(() => {
    // In the fetchProductItems function
    // Inside MaterialReceiptNoteFormModal
const fetchProductItems = async () => {
  try {
    // console.log("Fetching product items...");
    
    // UPDATED: Use the new /products endpoint
    const response = await fetch(`${API_URL}/products`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    
    if (!response.ok) throw new Error("Failed to fetch product items");
    const data = await response.json();
    
    // console.log("Product items received:", data);
    
    if (data && data.length > 0) {
      // console.log("First product structure:", data[0]);
      // console.log("Tracking Type for first product:", data[0].AllowProductTracking);
    }
    
    // The new backend returns the array directly or result.recordset
    setProductItems(data || []); 
    
  } catch (error) {
    // console.error("Error fetching product items:", error);
    // showToast(error.message || "Error fetching products", "error");
    setProductItems([]);
  }
};
    
    if (isOpen) {
      fetchProductItems();
    }
  }, [isOpen]);
  
  // Add this useEffect hook
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      // Check for Alt+P - Multi-select products
      if (e.altKey && e.key === 'p') {
        e.preventDefault();
        setShowMultiSelectModal(true);
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Add these functions
  const handleMultiSelectProducts = (selectedProductNames) => {
    if (selectedProductNames.length === 0) return;
    
    const newItems = selectedProductNames.map(productName => {
      const product = productItems.find(p => p.ProductName === productName);
      if (!product) return { ...initialItem };
      
      return {
        ...initialItem,
         tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        productName: product.ProductName,
        productCode: product.ProductCode || "",
        uom: product.UOMName || "",
        productTrackingType: product.AllowProductTracking || 0,
        tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        isFromPO: false,
        department_id: showDepartmentItemField ? "" : undefined,
        originalReceivedQuantity: 0,
        originalAcceptedQuantity: 0,
        availableReceivedQuantity: Infinity,
        availableAcceptedQuantity: Infinity,
        orderedQuantity: "",
        remainingQuantity: "",
        purchaseOrderNumber: "",
        itemCode: "",
        unitPrice: "0",
        discount: "0",
        taxCode: "",
        taxValue: "0.00",
        total: "0.00",
        batchNumber: "",
        expiryDate: "",
        serialNumbers: [],
      };
    });
    
    // Append new items to existing items
    setItems(prevItems => [...prevItems, ...newItems]);
  };

  // In MaterialReceiptNoteFormModal component
  const autofillSelectedProducts = (selectedProductObjects) => {
    if (selectedProductObjects.length === 0) return;
    
    try {
      const newItems = selectedProductObjects.map(product => {
        if (!product) return { ...initialItem };
        
        return {
          ...initialItem,
          productName: product.ProductName || "",
          productCode: product.ProductCode || "",
          uom: product.UOMName || "",
           productTrackingType: product.productTrackingType ?? product.ProductTrackingType ?? product.AllowProductTracking ?? 0,
          tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          isFromPO: false,
          department_id: showDepartmentItemField ? "" : undefined,
          originalReceivedQuantity: 0,
          originalAcceptedQuantity: 0,
          availableReceivedQuantity: Infinity,
          availableAcceptedQuantity: Infinity,
          orderedQuantity: "",
          remainingQuantity: "",
          purchaseOrderNumber: "",
          itemCode: "",
          unitPrice: "0",
          discount: "0",
          taxCode: "",
          taxValue: "0.00",
          total: "0.00",
          batchNumber: "",
          expiryDate: "",
          serialNumbers: [],
        };
      });
      
      // Append new items to existing items
      setItems(prevItems => [...prevItems, ...newItems]);
      
      return { success: true, count: selectedProductObjects.length };
    } catch (error) {
      // console.error('Error in autofillSelectedProducts:', error);
      return { success: false, error: error.message };
    }
  };
  
  // Update the useEffect that runs when productItems change
useEffect(() => {
  if (productItems.length > 0) {
    setItems((prevItems) =>
      prevItems.map((item) => {
        // Find the product by product code first, then by product name
        const selectedProduct = productItems.find(
          (p) => 
            (item.productCode && p.ProductCode === item.productCode) ||
            (item.productName && p.ProductName === item.productName)
        );
        
        let productTrackingType;
        if (item.MRNItemNumber) {
          // Existing item: preserve the productTrackingType from the MRN item
          // Ensure it's a number
          productTrackingType = typeof item.productTrackingType === 'number' 
            ? item.productTrackingType 
            : parseInt(item.productTrackingType) || 0;
        } else {
          // New item: use the product master
          productTrackingType = selectedProduct ? 
            (typeof selectedProduct.AllowProductTracking === 'number' 
              ? selectedProduct.AllowProductTracking 
              : parseInt(selectedProduct.AllowProductTracking) || 0) 
            : 0;
        }
        
        // console.log(`Updating item ${item.MRNItemNumber || 'new'} tracking type to:`, productTrackingType);
        
        return {
          ...item,
          productCode: item.productCode || (selectedProduct ? selectedProduct.ProductCode || "" : ""),
          uom: item.uom || (selectedProduct ? selectedProduct.UOMName || "" : ""),
          productTrackingType: productTrackingType,
          // If product doesn't allow batch but we have batch data, keep it for display
          ...(productTrackingType !== 1 ? { 
            batchNumber: item.batchNumber || "", 
            expiryDate: item.expiryDate || "" 
          } : {}),
          // If product doesn't allow serial but we have serial data, keep it for display
          ...(productTrackingType !== 2 ? { 
            serialNumbers: item.serialNumbers || [] 
          } : {}),
        };
      })
    );
  }
}, [productItems]);
  
  useEffect(() => {
    if (isOpen) {
      // Reset maximized state when modal opens
      setIsMaximized(false);
      
      // Fetch current user when modal opens
      fetchCurrentUser();
      fetchScreenConfig();
      fetchDropdownData();
      // console.log(
      //   `Modal opened in ${mode} mode with initial data:`,
      //   initialData
      // );
      if (mode === "edit" && initialData) {
         if (typeof initialData === 'string' || typeof initialData === 'number') {
          const mrnNumber = initialData; // It's the ID
          fetchMRNDetails(mrnNumber);
          setErrors({});
        } else {
          setMRNDataFromInitial(initialData);
        }
      } else {
        setHeader(initialHeader);
        setItems([]);
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen, mode, initialData]);
  
const fetchBatchData = async (mrnDocumentId, mrnItemId, productCode, locationCode) => {
  try {
    if (!mrnDocumentId || !mrnItemId || !productCode || !locationCode) return [];
    
    const encodedMrnDocumentId = encodeURIComponent(mrnDocumentId);
    const encodedMrnItemId = encodeURIComponent(mrnItemId);
    const encodedProductCode = encodeURIComponent(productCode);
    const encodedLocationCode = encodeURIComponent(locationCode);
    
    // UPDATED URL STRUCTURE
    const url = `${API_URL}/mrn/${encodedMrnDocumentId}/items/${encodedMrnItemId}/batch?productCode=${encodedProductCode}&locationCode=${encodedLocationCode}`;
    
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
    });
    
    if (!response.ok) throw new Error(`Failed to fetch batch data: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching batch data:', error);
    return [];
  }
};

const fetchSerialData = async (mrnDocumentId, mrnItemId, productCode, locationCode) => {
  try {
    if (!mrnDocumentId || !mrnItemId || !productCode || !locationCode) return [];
    
    const encodedMrnDocumentId = encodeURIComponent(mrnDocumentId);
    const encodedMrnItemId = encodeURIComponent(mrnItemId);
    const encodedProductCode = encodeURIComponent(productCode);
    const encodedLocationCode = encodeURIComponent(locationCode);
    
    // UPDATED URL STRUCTURE
    const url = `${API_URL}/mrn/${encodedMrnDocumentId}/items/${encodedMrnItemId}/serials?productCode=${encodedProductCode}&locationCode=${encodedLocationCode}`;
    
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
    });
    
    if (!response.ok) throw new Error(`Failed to fetch serial data: ${response.status}`);
    return response.json();
  } catch (error) {
    console.error('Error fetching serial data:', error);
    return [];
  }
};

// Now update the fetchMRNDetails function
const fetchMRNDetails = async (id) => {
  try {
    setLoading(true);
    // console.log(`Fetching MRN details for ID: ${id}`);
    const response = await fetch(`${API_URL}/mrn/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch MRN details: ${response.status}`);
    }
    const data = await response.json();
    // console.log("Fetched MRN details:", data);

    // 1. Ensure we are working with an array (handling the single object response from API)
    // The API returns a single object: { MRNNumber: ..., Items: [...] }
    const dataArray = Array.isArray(data) ? data : [data];

    if (dataArray.length === 0) {
      // console.warn("No data received for MRN");
      return;
    }

    // 2. Extract Header details from the first record
    const firstRecord = dataArray[0];
    
    const newHeader = {
      mrnDocumentId: firstRecord.MRNDocumentId || null,
      mrnNumber: firstRecord.MRNNumber || firstRecord.mrnNumber || "",
      mrnDate: firstRecord.MRNDate
        ? new Date(firstRecord.MRNDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
       eWayBillNumber: firstRecord.EWayBillNumber || "",
      vehicleNumber: firstRecord.VehicleNumber || "",
      transporter: firstRecord.Transporter || "",
      grNumber: firstRecord.GRNumber || "",
      deliveryNoteNumber:
        firstRecord.DeliveryNoteNumber || firstRecord.deliveryNoteNumber || "",
      challanNumber: firstRecord.ChallanNumber || firstRecord.challanNumber || "",
      vendorCode: firstRecord.VendorCode || firstRecord.vendorCode || "",
      vendorName: firstRecord.VendorName || firstRecord.vendor_name || "",
      receivedBy: firstRecord.ReceivedBy || firstRecord.receivedBy || "",
      remarks: firstRecord.Remarks || firstRecord.remarks || "",
      status:
        firstRecord.Status !== undefined && firstRecord.Status !== null
          ? Number(firstRecord.Status)
          : 1,
      pendingBillNumber:
        firstRecord.PendingBillNumber || firstRecord.pendingBillNumber || "",
      billDate: firstRecord.BillDate
        ? new Date(firstRecord.BillDate).toISOString().split("T")[0]
        : "",
       vendorInvoiceNo: firstRecord.VendorInvoiceNo || "",
  invoiceReceivedDate: firstRecord.InvoiceReceivedDate
    ? new Date(firstRecord.InvoiceReceivedDate).toISOString().split("T")[0]
    : "",
      inventoryFlag: firstRecord.InventoryFlag !== undefined 
        ? (firstRecord.InventoryFlag ? "yes" : "no") 
        : "no",
      branchCode: firstRecord.BranchCode || "",
      department_id: firstRecord.DepartmentID?.toString() || "",
        locationCode: firstRecord.LocationCode || "", 
        projectCode: firstRecord.ProjectCode || "",
         attachment: firstRecord.Attachment || "",
      attachmentFileName: firstRecord.AttachmentFileName || "",
    };
    
    // console.log("Setting header:", newHeader);
    setHeader(newHeader);
    
    // 3. Flatten Items from the response
    // Since we fetched by ID, the API returns exactly the items for this MRN.
    // We do NOT need to filter by MRNNumber locally anymore.
    let flatItems = [];
    dataArray.forEach(record => {
      if (record.Items && Array.isArray(record.Items)) {
        flatItems = flatItems.concat(record.Items);
      }
    });

    // console.log(`Found ${flatItems.length} items for MRN ID ${id}.`);

    // 4. Process items directly (No filter needed)
    if (flatItems.length > 0) {
      
      const mappedItems = flatItems.map((item) => {
        // console.log("Mapping MRN item:", item);
        
        const orderedQty = parseFloat(item.OrderedQty || item.orderedQuantity) || 0;
        const receivedQty = parseFloat(item.ReceivedQty || item.receivedQuantity) || 0;
        const acceptedQty = parseFloat(item.AcceptedQty || item.acceptedQuantity) || 0;
        const poiRemaining = parseFloat(item.OutstandingQty || item.outstandingQuantity) || 0;
        
        let productTrackingType = 0; 
        if (item.ProductTrackingType !== null && item.ProductTrackingType !== undefined) {
          productTrackingType = parseInt(item.ProductTrackingType);
        }
        
        // console.log(`Item ${item.MRNItemNumber} tracking type:`, productTrackingType);
        
        return {
          mrnItemId: item.MRNItemID || null, // Added ID
          itemCode: item.ItemCode || item.itemCode || "",
          productName:
            item.ProductName ||
            item.productName ||
            item.ItemName ||
            item.itemName ||
            "",
          productCode: item.ProductCode || item.productCode || "",
          orderedQuantity: orderedQty.toString(),
          remainingQuantity: poiRemaining.toString(),
          receivedQuantity: receivedQty.toString(),
           shortageQty: (item.ShortageQty || "0").toString(),
          uom: item.UOM || item.uom || "",
            poLineNumber: item.POLineNumber  || "", 
          acceptedQuantity: acceptedQty.toString(),
          rejectedQuantity: (receivedQty - acceptedQty).toString(),
          warehouseCode:
            item.WarehouseCode ||
            item.warehouseCode ||
            item.Warehouse ||
            item.warehouse ||
            "",
            branchCode: item.BranchCode || "",  
           branchName: item.BranchName || "", 
           projectCode: item.ProjectCode || "", 
          attachment: item.Attachment || item.attachment || "",
          attachmentFileName:
            item.AttachmentFileName || item.attachmentFileName || "",
          remarks: item.Remarks || item.remarks || "",
          MRNItemNumber: item.MRNItemNumber || "",
          tempId:
            item.tempId ||
            `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          purchaseOrderNumber:
            item.PurchaseOrderNumber || item.purchaseOrderNumber || "",
          MRNNumber: item.MRNNumber || id, // Fallback to ID
          isFromPO: !!(item.PurchaseOrderNumber || item.purchaseOrderNumber),
          department_id: item.DepartmentID?.toString() || "",
          originalReceivedQuantity: receivedQty,
          originalAcceptedQuantity: acceptedQty,
          availableReceivedQuantity: poiRemaining + receivedQty,
          availableAcceptedQuantity: poiRemaining + acceptedQty,
          unitPrice: item.UnitPrice ? item.UnitPrice.toString() : "0",
          discount: item.Discount ? item.Discount.toString() : "0",
          taxCode: item.TaxCode || "",
          taxValue: item.TaxValue ? item.TaxValue.toString() : "0.00",
          total: item.Total ? item.Total.toString() : "0.00",
          productTrackingType: productTrackingType,
           batches: item.Batches || [], 
          serialNumbers: item.SerialNumbers || [],
        };
      });
      
      // Fetch tracking data for items
      const itemsWithTrackingData = await Promise.all(
        mappedItems.map(async (item) => {
          // IMPORTANT: Pass the ID (id) and Item ID (item.MRNItemID)
          if (item.mrnItemId && (item.productTrackingType === 1 || item.productTrackingType === 2) && item.productCode && item.warehouseCode) {
            try {
              if (item.productTrackingType === 1) {
                const batchData = await fetchBatchData(
                  id, // Pass MRN Document ID
                  item.mrnItemId, // Pass MRN Item ID
                  item.productCode,
                  item.warehouseCode
                );
                const batches = batchData.map(batch => ({
                  batchNumber: batch.BatchNumber,
                  expiryDate: batch.ExpiryDate ? new Date(batch.ExpiryDate).toISOString().split('T')[0] : "",
                  quantity: batch.Quantity.toString()
                }));
                return { ...item, batches };
              } else if (item.productTrackingType === 2) {
                const serialData = await fetchSerialData(
                  id, // Pass MRN Document ID
                  item.mrnItemId, // Pass MRN Item ID
                  item.productCode,
                  item.warehouseCode
                );
                const serialNumbers = serialData.map(serial => serial.SerialNumber);
                return { ...item, serialNumbers };
              }
            } catch (error) {
              console.error('Error fetching tracking data for item:', item.MRNItemID, error);
              return item;
            }
          }
          return item;
        })
      );
      
      setItems(itemsWithTrackingData);
    } else {
      setItems([]);
    }
    
    if (newHeader.vendorCode) {
      fetchPurchaseOrders(newHeader.vendorCode);
    }
  } catch (error) {
    console.error("Error fetching MRN details:", error);
    // Fallback logic if needed, or just set empty state
    setHeader(initialHeader);
    setItems([]);
  } finally {
    setLoading(false);
  }
};

  const setMRNDataFromInitial = (initialData) => {
    // console.log("Using fallback: setting MRN data from initial data");
    const newHeader = {
      mrnDocumentId: initialData.MRNDocumentId || null,
      mrnNumber: initialData.MRNNumber || initialData.mrnNumber || "",
      mrnDate: initialData.MRNDate
        ? new Date(initialData.MRNDate).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0],
      deliveryNoteNumber:
        initialData.DeliveryNoteNumber || initialData.deliveryNoteNumber || "",
      challanNumber:
        initialData.ChallanNumber || initialData.challanNumber || "",
      vendorCode: initialData.VendorCode || initialData.vendorCode || "",
      vendorName: initialData.VendorName || initialData.vendorName || "",
      purchaseOrderNumber:
        initialData.PurchaseOrderNumber ||
        initialData.purchaseOrderNumber ||
        "",
        
      receivedBy: initialData.ReceivedBy || initialData.receivedBy || "",
       vendorInvoiceNo: initialData.VendorInvoiceNo || "",
  invoiceReceivedDate: initialData.InvoiceReceivedDate
    ? new Date(initialData.InvoiceReceivedDate).toISOString().split("T")[0]
    : "",

      remarks: initialData.Remarks || initialData.remarks || "",
      status:
        initialData.Status !== undefined && initialData.Status !== null
          ? Number(initialData.Status)
          : 1,
      pendingBillNumber: initialData.PendingBillNumber || initialData.pendingBillNumber || "",
      billDate: initialData.BillDate
        ? new Date(initialData.BillDate).toISOString().split("T")[0]
        : "",
      inventoryFlag: initialData.InventoryFlag !== undefined 
        ? (initialData.InventoryFlag ? "yes" : "no") 
        : "no",
      branchCode: initialData.BranchCode || "",
      department_id: initialData.DepartmentID?.toString() || "",
            locationCode: initialData.LocationCode || "", 
            projectCode: initialData.ProjectCode || "",
    };
    setHeader(newHeader);
    if (
      initialData.Items &&
      Array.isArray(initialData.Items) &&
      initialData.Items.length > 0
    ) {
      // console.log("Setting items from initial data:", initialData.Items);
      
      const mappedItems = initialData.Items.map((item) => {
        // console.log("Mapping initial item:", item);
        
        const orderedQty = parseFloat(item.OrderedQty || item.orderedQuantity) || 0;
        const receivedQty = parseFloat(item.ReceivedQty || item.receivedQuantity) || 0;
        const acceptedQty = parseFloat(item.AcceptedQty || item.acceptedQuantity) || 0;
        const poiRemaining = parseFloat(item.OutstandingQty || item.outstandingQuantity || 0);
        
        // Find the product to get AllowProductTracking
        const selectedProduct = productItems.find(p => p.ProductCode === item.ProductCode);
        const hasBatchData = !!(item.BatchNumber && item.BatchNumber.trim() !== '');
        
        return {
          mrnItemId: item.MRNItemID || null,
          itemCode: item.ItemCode || item.itemCode || "",
          productName:
            item.ProductName ||
            item.productName ||
            item.ItemName ||
            item.itemName ||
            "",
            productCode: item.ProductCode || item.productCode || "",
             poLineNumber: item.LineNumber || item.ItemCode || "",
          orderedQuantity: orderedQty.toString(),
          remainingQuantity: poiRemaining.toString(),
          receivedQuantity: receivedQty.toString(),
           shortageQty: (item.ShortageQty || item.shortageQty || "0").toString(),
          uom: item.UOM || item.uom || "",
          acceptedQuantity: acceptedQty.toString(),
          rejectedQuantity: (receivedQty - acceptedQty).toString(),
          warehouseCode:
            item.WarehouseCode ||
            item.warehouseCode ||
            item.Warehouse ||
            item.warehouse ||
            "",
             branchCode: item.BranchCode || "",  // <-- ADD THIS LINE
            branchName: item.BranchName || "",
             projectCode: item.ProjectCode || "",
          attachment: item.Attachment || item.attachment || "",
          attachmentFileName:
            item.AttachmentFileName || item.attachmentFileName || "",
          remarks: item.Remarks || item.remarks || "",
          MRNItemNumber: item.MRNItemNumber || "",
          tempId:
            item.tempId ||
            `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          purchaseOrderNumber:
            item.PurchaseOrderNumber ||
            item.purchaseOrderNumber ||
            newHeader.purchaseOrderNumber ||
            "",
          MRNNumber:
            item.MRNNumber ||
            initialData.MRNNumber ||
            initialData.mrnNumber ||
            newHeader.mrnNumber ||
            "",
          isFromPO: !!(item.PurchaseOrderNumber || item.purchaseOrderNumber),
          department_id: item.DepartmentID?.toString() || "",
          originalReceivedQuantity: receivedQty,
          originalAcceptedQuantity: acceptedQty,
          availableReceivedQuantity: poiRemaining + receivedQty,
          availableAcceptedQuantity: poiRemaining + acceptedQty,
          unitPrice: item.UnitPrice ? item.UnitPrice.toString() : "0",
          discount: item.Discount ? item.Discount.toString() : "0",
          taxCode: item.TaxCode || "",
          taxValue: item.TaxValue ? item.TaxValue.toString() : "0.00",
          total: item.Total ? item.Total.toString() : "0.00",
          // Product tracking fields
          productTrackingType: item.ProductTrackingType || 0,
          batchNumber: item.BatchNumber || "",
          expiryDate: item.ExpiryDate ? new Date(item.ExpiryDate).toISOString().split('T')[0] : "",
          serialNumbers: item.SerialNumbers || [],
        };
      });
      
      // console.log("Mapped initial items:", mappedItems);
      setItems(mappedItems);
    } else {
      // console.log("No items found in initial data, setting empty array");
      setItems([]);
    }
    if (newHeader.vendorCode) {
      fetchPurchaseOrders(newHeader.vendorCode);
    }
  };
  
const fetchVendors = async () => {
  try {
    const response = await fetch(`${API_URL}/vendors`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error("Failed to fetch vendors");
    const data = await response.json();
    
    // FIX: Explicitly map and flatten data
    return data.map(v => ({
      ...v, // Keep all original data
      
      // Map for Dropdown value and label
      vendorNo: v.vendor_code,
      vendorName: v.vendor_name,

      // Flatten Shipping Address for "Supplier Details" card
      // Priority: Shipping Address -> Billing Address -> N/A
      address:(v.addresses && v.addresses.length > 0)
      ? v.addresses[0].address_line1 
      : 'N/A',

      // Flatten Phone from Contacts for "Supplier Details" card
      phone: (v.contacts && v.contacts.length > 0)
        ? v.contacts[0].phone_number
        : 'N/A'
    }));
  } catch (error) {
    // console.error("Error fetching vendors:", error);
    // alert(`Error loading vendors: ${error.message}`);
    showToast("Error loading vendors", "error");
    return [];
  }
};

const fetchLocations = async () => {
  try {
    const response = await fetch(`${API_URL}/locations-filter`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) {
      throw new Error(`Failed to fetch locations: ${response.status}`);
    }
    const data = await response.json();

    // FIX: Explicitly map PascalCase (Backend) to CamelCase (Frontend)
    // This fixes the "undefined" issue in the dropdown and card
    return data.map(loc => ({
      ...loc, // Keep all original data
      locationCode: loc.LocationCode, // Map for Dropdown value
      locationName: loc.LocationName, // Map for Dropdown label
      
      // Map these fields for the "Receipt Location Details" card display
      address: loc.AddressLine1 
        ? (loc.AddressLine2 ? `${loc.AddressLine1}, ${loc.AddressLine2}` : loc.AddressLine1) 
        : 'N/A',
      city: loc.City || 'N/A',
      state: loc.State || 'N/A'
    }));
  } catch (error) {
    // console.error("Error fetching locations:", error);
    // alert(`Error loading locations: ${error.message}`);
    showToast("Error loading locations", "error");
    return [];
  }
};

const fetchBranches = async () => {
  try {
    const response = await fetch(`${API_URL}/branches-filter`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error("Failed to fetch branches");
    const data = await response.json();
    return data || [];
  } catch (error) {
    // console.error("Error fetching branches:", error);
    return [];
  }
};

const fetchProjects = async () => {
  try {
    const response = await fetch(`${API_URL}/project-filter`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
    });
    if (!response.ok) throw new Error("Failed to fetch projects");
    return await response.json();
  } catch (error) {
    console.error("Error fetching projects:", error);
    return [];
  }
};

const fetchTaxCodes = async () => {
  try {
    const response = await fetch(`${API_URL}/taxes`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error("Failed to fetch tax codes");
    const data = await response.json();
    return data || [];
  } catch (error) {
    // console.error("Error fetching tax codes:", error);
    return [];
  }
};

const fetchNextMRNNumber = async () => {
  try {
    const response = await fetch(`${API_URL}/mrn/next-number`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
    if (!response.ok) throw new Error("Failed to fetch next MRN number");
    const data = await response.json();
    return data.nextMRNNumber || "";
  } catch (error) {
    // console.error("Error fetching next MRN number:", error);
    return "";
  }
};

const fetchDropdownData = async () => {
  try {
    setLoading(true);
    
    // Fetch all data in parallel
    const [vendors, locations, branches,projects, taxCodes, nextMRNNumber] = await Promise.all([
  fetchVendors(),
  fetchLocations(),   // ← This will now use the correct one
  fetchBranches(),
  fetchProjects(),
  fetchTaxCodes(),
  fetchNextMRNNumber()
]);
    
    setDropdownData({
      vendors,
      locations,
      nextMRNNumber,
      branches,
      projects
    });
    
    // Set tax codes
    setTaxCodes(taxCodes);
    
    if (mode !== "edit") {
      setHeader((prev) => ({
        ...prev,
        mrnNumber: nextMRNNumber,
      }));
    }
  } catch (error) {
    // console.error("Error fetching dropdown data:", error);
    // alert(`Error loading form data: ${error.message}`);
    showToast("Error loading form data", "error");
  } finally {
    setLoading(false);
  }
};
  
  const fetchPurchaseOrders = async (vendorCode) => {
    try {
      setLoading(true);
      // console.log(`Fetching purchase orders for vendor: ${vendorCode}`);
      const response = await fetch(
        `${API_URL}/purchase-order/vendor/${vendorCode}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }}
      );
      if (!response.ok) throw new Error("Failed to fetch purchase orders");
      const data = await response.json();
      // console.log(`Received ${data.length} purchase orders:`, data);
      setPurchaseOrders(data || []);
    } catch (error) {
      // console.error("Error fetching purchase orders:", error);
      setPurchaseOrders([]);
    } finally {
      setLoading(false);
    }
  };
  
  const handleHeaderChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;
    if (name === "status") {
      newValue = Number(value);
    }
    if (name === "inventoryFlag") {
      newValue = value;
      // console.log(`Inventory Flag changed to: ${newValue === 'yes' ? 'Yes' : 'No'}`);
    }
    if (name === "locationCode") {
  setHeader({ ...header, [name]: value });
}
    setHeader({ ...header, [name]: newValue });
    
    if (name === "vendorCode") {
  const selectedVendor = dropdownData.vendors.find(
    (v) => v.vendorNo === value
  );
  if (selectedVendor) {
    setHeader((prev) => ({
      ...prev,
      vendorName: selectedVendor.vendorName || "",
      // Optional: store extra info if you add fields later
      // vendorAddress: selectedVendor.address || "",
      // vendorCity: selectedVendor.city || "",
      // vendorPhone: selectedVendor.phone || "",
    }));
  } else {
    setHeader((prev) => ({
      ...prev,
      vendorName: "",
    }));
  }

  if (value) {
    fetchPurchaseOrders(value); // Still use vendorNo as vendorCode for POs
  } else {
    setPurchaseOrders([]);
    if (mode !== "edit") {
      setItems([]);
    }
  }
}
    
    if (name === "purchaseOrderNumber") {
      if (value) {
        if (mode !== "edit") {
          fetchPurchaseOrderItems(value);
        }
      } else {
        if (mode !== "edit") {
          setItems([]);
        }
      }
    }
  };
  
  const fetchPurchaseOrderItems = async (poNumber) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/purchase-order/${poNumber}`,{headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }});
      if (!response.ok) throw new Error("Failed to fetch purchase order items");
      const data = await response.json();
      
      if (data.Items && data.Items.length > 0) {
        if (mode === "edit") {
          setItems((prevItems) => {
            return data.Items.map((poItem) => {
              const mrnItem = prevItems.find(
                (item) =>
                  item.itemCode === poItem.ItemCode ||
                  item.productName === poItem.ProductName
              );
              if (mrnItem) {
                return {
                  ...mrnItem,
                  itemCode: poItem.ItemCode || mrnItem.itemCode,
                  productName: poItem.ProductName || mrnItem.productName,
                  productCode: poItem.ProductCode || mrnItem.productCode,
                  orderedQuantity: poItem.Quantity
                    ? poItem.Quantity.toString()
                    : mrnItem.orderedQuantity,
                  remainingQuantity: poItem.OutstandingQty
                    ? poItem.OutstandingQty.toString()
                    : poItem.OutstandingQty,
                  uom: poItem.UOM || mrnItem.uom,
                  warehouseCode: poItem.LocationCode || mrnItem.warehouseCode,
                  purchaseOrderNumber: poNumber,
                  isFromPO: true,
                  unitPrice: poItem.UnitPrice ? poItem.UnitPrice.toString() : mrnItem.unitPrice,
                  discount: poItem.Discount ? poItem.Discount.toString() : mrnItem.discount,
                  taxCode: poItem.TaxCode || mrnItem.taxCode,
                  taxValue: mrnItem.taxValue,
                  total: mrnItem.total,
                  // Preserve tracking fields if they exist
                  productTrackingType: mrnItem.productTrackingType || 0,
                  batchNumber: mrnItem.batchNumber || "",
                  expiryDate: mrnItem.expiryDate || "",
                  serialNumbers: mrnItem.serialNumbers || [],
                };
              } else {
                // Find the product to get AllowProductTracking
                const selectedProduct = productItems.find(p => p.ProductCode === poItem.ProductCode);
                
                return {
                  itemCode: poItem.ItemCode || "",
                  productName: poItem.ProductName || "",
                  productCode: poItem.ProductCode,
                  orderedQuantity: poItem.Quantity
                    ? poItem.Quantity.toString()
                    : "",
                  remainingQuantity: poItem.OutstandingQty
                    ? poItem.OutstandingQty.toString()
                    : "",
                  receivedQuantity: "",
                  uom: poItem.UOM || "",
                  acceptedQuantity: "",
                  rejectedQuantity: "0",
                  warehouseCode: poItem.LocationCode || "",
                  attachment: "",
                  attachmentFileName: "",
                  remarks: "",
                  tempId: `temp-${Date.now()}-${Math.random()
                    .toString(36)
                    .substr(2, 9)}`,
                  purchaseOrderNumber: poNumber,
                  isFromPO: true,
                  unitPrice: poItem.UnitPrice ? poItem.UnitPrice.toString() : "0",
                  discount: poItem.Discount ? poItem.Discount.toString() : "0",
                  taxCode: poItem.TaxCode || "",
                  taxValue: "0.00",
                  total: "0.00",
                  // Initialize tracking fields
                  productTrackingType: selectedProduct ? selectedProduct.AllowProductTracking || 0 : 0,
                  batchNumber: "",
                  expiryDate: "",
                  serialNumbers: [],
                };
              }
            });
          });
        } else {
          const newItems = data.Items.map((item) => {
            // Find the product to get AllowProductTracking
            const selectedProduct = productItems.find(p => p.ProductCode === item.ProductCode);
            
            return {
              itemCode: item.ItemCode || "",
              productName: item.ProductName || "",
              productCode: item.ProductCode,
              orderedQuantity: item.Quantity ? item.Quantity.toString() : "",
              remainingQuantity: item.OutstandingQty
                ? item.OutstandingQty.toString()
                : "",
              receivedQuantity: "",
              uom: item.UOM || "",
              acceptedQuantity: "",
              rejectedQuantity: "0",
              warehouseCode: item.LocationCode || "",
              attachment: "",
              attachmentFileName: "",
              remarks: "",
              tempId: `temp-${Date.now()}-${Math.random()
                .toString(36)
                .substr(2, 9)}`,
              purchaseOrderNumber: poNumber,
              isFromPO: true,
              unitPrice: item.UnitPrice ? item.UnitPrice.toString() : "0",
              discount: item.Discount ? item.Discount.toString() : "0",
              taxCode: item.TaxCode || "",
              taxValue: "0.00",
              total: "0.00",
              // Initialize tracking fields
              productTrackingType: selectedProduct ? selectedProduct.AllowProductTracking || 0 : 0,
              batchNumber: "",
              expiryDate: "",
              serialNumbers: [],
            };
          });
          setItems(newItems);
        }
      } else {
        setItems([]);
      }
    } catch (error) {
      // console.error("Error fetching purchase order items:", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  
const handleItemChange = (idx, e) => {
  const { name, value } = e.target;
  const newItems = [...items];
  // console.log("changing the item field", name, "to", value, "for item index", idx);
  // console.log("Current item before change:", newItems[idx]);
  
  // Skip changes for PO-linked items on certain fields
  if (newItems[idx].isFromPO && (
  name === "productName" || 
  name === "itemCode" || 
  name === "orderedQuantity" || 
  name === "remainingQuantity" || 
  name === "uom" || 
  name === "warehouseCode" ||
  name === "unitPrice" || 
  name === "discount" || 
  name === "taxCode"
)) {
    return;
  }
  
  // For manual items, prevent changes to PO-related fields
  if (!newItems[idx].isFromPO && (
    name === "orderedQuantity" || 
    name === "remainingQuantity" || 
    name === "purchaseOrderNumber" || 
    name === "itemCode"
  )) {
    return;
  }
    
  newItems[idx][name] = value;
  
  // If product name is changed, auto-fill UOM, product code, and tracking type
 if (name === "productName") {
  // console.log("Product name changed to:", value);
  // console.log("Available products:", productItems);
  
  const selectedProduct = productItems.find(product => 
    product.ProductName === value || product.productName === value
  );
  
  // console.log("Selected product:", selectedProduct);
  
  if (selectedProduct) {
    // Update UOM using UOMName from the joined query result
    newItems[idx].uom = selectedProduct.UOMName || selectedProduct.uomName || "";
    // Update product code
    newItems[idx].productCode = selectedProduct.ProductCode || selectedProduct.productCode || "";
    // Update tracking type - ensure it's a number
    newItems[idx].productTrackingType = typeof selectedProduct.AllowProductTracking === 'number' 
      ? selectedProduct.AllowProductTracking 
      : parseInt(selectedProduct.AllowProductTracking) || 0;
    
    // Initialize batches array
    newItems[idx].batches = [];
    
    // console.log("Setting UOM to:", newItems[idx].uom);
    // console.log("Setting productTrackingType to:", newItems[idx].productTrackingType);
    
    // Reset tracking fields based on tracking type
    if (newItems[idx].productTrackingType === 1) { // Batch
      // Show batch modal
      setCurrentBatchItem(idx);
      setShowBatchModal(true);
    } else if (newItems[idx].productTrackingType === 2) { // Serial
      // Reset serial numbers, will show modal when quantity is set
      newItems[idx].serialNumbers = [];
    } else { // None
      newItems[idx].batchNumber = "";
      newItems[idx].expiryDate = "";
      newItems[idx].serialNumbers = [];
    }
  } else {
    newItems[idx].productCode = ""; // Ensure not null
    newItems[idx].productTrackingType = 0;
    newItems[idx].batchNumber = "";
    newItems[idx].expiryDate = "";
    newItems[idx].serialNumbers = [];
    newItems[idx].batches = [];
  }
}
  
  // Handle accepted quantity change for serial tracking
  if (name === "acceptedQuantity" && newItems[idx].productTrackingType === 2) {
    const qty = parseInt(value) || 0;
    if (qty > 0) {
      setCurrentSerialItem(idx);
      // setShowSerialModal(true);
    }
  }
  
  // Recalculate tax and total for manual items when relevant fields change
  if (!newItems[idx].isFromPO && 
      (name === "unitPrice" || name === "discount" || name === "taxCode" || name === "acceptedQuantity")) {
    const totals = calculateItemTotal(newItems[idx]);
    newItems[idx].taxValue = totals.taxValue;
    newItems[idx].total = totals.total;
  }
  
  // Recalculate tax and total for PO items when accepted quantity changes
  if (newItems[idx].isFromPO && name === "acceptedQuantity") {
    const totals = calculateItemTotal(newItems[idx]);
    newItems[idx].taxValue = totals.taxValue;
    newItems[idx].total = totals.total;
  }
  
  // Rest of your existing code for quantity validation
  // console.log(`Item ${idx} changed:`, newItems[idx]);
  const orderedQty = parseFloat(newItems[idx].orderedQuantity) || 0;
  let receivedQty = parseFloat(newItems[idx].receivedQuantity) || 0;
  let acceptedQty = parseFloat(newItems[idx].acceptedQuantity) || 0;
  
  if (name === "receivedQuantity") {
  receivedQty = parseFloat(value) || 0;
  // Automatically set accepted quantity equal to received quantity
  acceptedQty = receivedQty;
  newItems[idx].acceptedQuantity = acceptedQty.toString();
}

  if (name === "acceptedQuantity") {
    acceptedQty = parseFloat(value) || 0;
  }

  const item = newItems[idx];
  if (item.isFromPO) {
    if (name === "receivedQuantity") {
      // Validate against available received
      const maxReceived = item.availableReceivedQuantity;
      // console.log(`Validating received quantity. Input: ${receivedQty}, Max: ${maxReceived}`);
      if (receivedQty > maxReceived) {
        newItems[idx].receivedQuantity = maxReceived.toString();
        receivedQty = maxReceived;
        setErrors((prev) => ({
          ...prev,
          [`receivedQuantity_${idx}`]: `Cannot exceed available received quantity (${maxReceived})`,
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`receivedQuantity_${idx}`];
          return newErrors;
        });
      }
    }

    if (name === "acceptedQuantity") {
      // Validate against available accepted and received
      const maxAccepted = Math.min(receivedQty, item.availableAcceptedQuantity);
      if (acceptedQty > maxAccepted) {
        newItems[idx].acceptedQuantity = maxAccepted.toString();
        acceptedQty = maxAccepted;
        setErrors((prev) => ({
          ...prev,
          [`acceptedQuantity_${idx}`]: `Cannot exceed ${receivedQty < item.availableAcceptedQuantity ? 'received' : 'available accepted'} quantity (${maxAccepted})`,
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`acceptedQuantity_${idx}`];
          return newErrors;
        });
      }
    }
  }

  // Ensure accepted quantity doesn't exceed received quantity (always)
  if (acceptedQty > receivedQty) {
    newItems[idx].acceptedQuantity = receivedQty.toString();
    acceptedQty = receivedQty;
  }

  // Calculate rejected quantity
  const rejectedQty = receivedQty - acceptedQty;
  newItems[idx].rejectedQuantity =
    rejectedQty >= 0 ? rejectedQty.toString() : "0";
    
  // console.log(`Item ${idx} changed:`, newItems[idx]);
  setItems(newItems);
};
  
  const handleSelectedPOItems = (selectedItems) => {
    const newItems = selectedItems.map((item) => {
      // Find the product to get AllowProductTracking
      const selectedProduct = productItems.find(p => p.ProductCode === item.ProductCode);
      
      // Calculate initial totals (acceptedQty is 0 initially, so total will be 0)
      const initialTotals = calculateItemTotal({
        unitPrice: item.UnitPrice?.toString() || "0",
        discount: item.Discount?.toString() || "0",
        taxCode: item.TaxCode || "",
        acceptedQuantity: "0"
      });

      // console.log("Selected PO item to add:", item.OutstandingQty);
      
      return {
        itemCode: item.ItemCode,
        productName: item.ProductName || item.productName,
        productCode: item.ProductCode,
        orderedQuantity: item.Quantity ? item.Quantity.toString() : "",
        remainingQuantity: item.OutstandingQty ? item.OutstandingQty.toString() : "",
        receivedQuantity: "",
        uom: item.UOM,
        acceptedQuantity: "",
        rejectedQuantity: "0",
        warehouseCode: item.LocationCode || "",
        attachment: "",
        attachmentFileName: "",
        remarks: "",
        tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        purchaseOrderNumber: item.PurchaseOrderNumber,
        isFromPO: true,
        department_id: "",
        originalReceivedQuantity: 0,
        originalAcceptedQuantity: 0,
        availableReceivedQuantity: parseFloat(item.OutstandingQty || 0),
        availableAcceptedQuantity: parseFloat(item.OutstandingQty || 0),
        // New fields - auto-fill from PO
        unitPrice: item.UnitPrice ? item.UnitPrice.toString() : "0",
        discount: item.Discount ? item.Discount.toString() : "0",
        taxCode: item.TaxCode || "",
        taxValue: initialTotals.taxValue,
        total: initialTotals.total,
        // Initialize tracking fields
        productTrackingType: selectedProduct ? selectedProduct.AllowProductTracking || 0 : 0,
        batchNumber: "",
        expiryDate: "",
        serialNumbers: [],
      };
    });
    const existingItemKeys = items.map(
      (item) => `${item.itemCode}-${item.purchaseOrderNumber}`
    );
    const uniqueNewItems = newItems.filter(
      (newItem) =>
        !existingItemKeys.includes(
          `${newItem.itemCode}-${newItem.purchaseOrderNumber}`
        )
    );
    if (uniqueNewItems.length > 0) {
      setItems((prev) => [...prev, ...uniqueNewItems]);
    }
  };

  const addManualItem = () => {
    const newItem = {
      ...initialItem,
      tempId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      isFromPO: false,
      department_id: showDepartmentItemField ? "" : undefined,
      originalReceivedQuantity: 0,
      originalAcceptedQuantity: 0,
      availableReceivedQuantity: Infinity,
      availableAcceptedQuantity: Infinity,
      orderedQuantity: "",
      remainingQuantity: "",
      purchaseOrderNumber: "",
      itemCode: "",
      // Initialize new fields for manual items
      unitPrice: "0",
      discount: "0",
      taxCode: "",
      taxValue: "0.00",
      total: "0.00",
      // Initialize tracking fields
      productTrackingType: 0,
      batchNumber: "",
      expiryDate: "",
      serialNumbers: [],
    };
    setItems([...items, newItem]);
  };
  
  const handleFileChange = (idx, e) => {
    const file = e.target.files[0];
    if (file) {
      const newItems = [...items];
      newItems[idx].attachmentFileName = file.name;
      setItems(newItems);
    }
  };
  
  const handleRemoveAttachment = (idx) => {
    const newItems = [...items];
    newItems[idx].attachment = "";
    newItems[idx].attachmentFileName = "";
    if (fileInputRefs.current[idx]) {
      fileInputRefs.current[idx].value = "";
    }
    setItems(newItems);
  };

    const handleHeaderFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeader(prev => ({
        ...prev,
        attachmentFileName: file.name,
        attachment: file.name // temporary indicator
      }));
    }
  };

  const handleRemoveHeaderAttachment = () => {
    setHeader(prev => ({
      ...prev,
      attachment: "",
      attachmentFileName: ""
    }));
    if (headerFileInputRef.current) {
      headerFileInputRef.current.value = "";
    }
  };

  const handleDownloadHeaderAttachment = async () => {
    try {
      if (!header.mrnNumber) {
        showToast("MRN Number not found", "error");
        return;
      }
      const response = await fetch(`${API_URL}/mrn/${header.mrnDocumentId}/header/file`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });

      if (!response.ok) throw new Error("Failed to download file");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = header.attachmentFileName || "attachment";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      showToast("Error downloading file", "error");
    }
  };
  
  const handleDownloadAttachment = async (idx) => {
    try {
      const item = items[idx];
      if (!item.MRNItemNumber || !header.mrnNumber) {
        // console.error("Missing MRN number or item number");
        // alert("Missing required information for download");
        showToast("Missing required information for download", "error");
        return;
      }
      const response = await fetch(
        `${API_URL}/mrn/${header.mrnNumber}/items/${item.MRNItemNumber}/file`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to download file");
      }
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = item.attachmentFileName || "attachment";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      // console.error("Error downloading file:", error);
      // alert(`Error downloading file: ${error.message}`);
      showToast(`Error downloading file: ${error.message}`, "error");
    }
  };
  
  const addItem = () => {
    if (header.vendorCode) {
      setShowPOItemsModal(true);
    } else {
      // alert("Please select a vendor first");
      showToast("Please select a vendor first", "warning");
    }
  };
  
  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };
  
  // Handlers for saving batch and serial information
 // Replace the existing handleSaveBatch function with:
const handleSaveBatches = (batches) => {
  if (currentBatchItem !== null) {
    const newItems = [...items];
    // Format the batches to ensure expiry date is in the correct format
    const formattedBatches = batches.map(batch => ({
      ...batch,
      expiryDate: batch.expiryDate ? new Date(batch.expiryDate).toISOString().split('T')[0] : ""
    }));
    newItems[currentBatchItem] = {
      ...newItems[currentBatchItem],
      batches: formattedBatches
    };
    setItems(newItems);
    setShowBatchModal(false);
    setCurrentBatchItem(null);
  }
};

  const handleSaveSerial = (serialNumbers) => {
    if (currentSerialItem !== null) {
      const newItems = [...items];
      newItems[currentSerialItem] = {
        ...newItems[currentSerialItem],
        serialNumbers
      };
      setItems(newItems);
      setShowSerialModal(false);
      setCurrentSerialItem(null);
    }
  };
  
   const validate = () => {
    const errs = {};
    
    // --- 1. Header Validation ---
    if (!header.mrnNumber || !header.mrnNumber.trim())
      errs.mrnNumber = "Required";
    // if (!header.mrnDate) errs.mrnDate = "Required";
    if (!header.vendorCode) errs.vendorCode = "Supplier Code is required";
    if (!header.receivedBy) errs.receivedBy = "Recieved by is required";
    if (!header.invoiceReceivedDate) errs.invoiceReceivedDate = "Invoice received Date is required";
    if(!header.vendorInvoiceNo) errs.vendorInvoiceNo = "Vendor invoice no is required";
    if(!header.locationCode) errs.locationCode = "Location code is required";
     if (!header.billDate) errs.billDate = "Bill date is required";
      if (!header.attachmentFileName) {
        errs.headerAttachment = "Header attachment is required";
    }
    
    // if (showDepartmentField && !header.department_id) errs.department_id = "Required";
    
    if (items.length === 0) {
      errs.items = "At least one item is required";
    }

    // --- 2. Item Field Validation ---
    items.forEach((item, idx) => {
      // if (!item.productName) errs[`productName_${idx}`] = "Product name is required";
      if (!item.productCode) errs[`productCode_${idx}`] = "Product code is required";
      
      
      //  if (!item.attachmentFileName) {
      //     errs[`attachment_${idx}`] = "Attachment is required";
      // }
      
      if (!item.receivedQuantity) errs[`receivedQuantity_${idx}`] = "Received Quantity is required";
      // if (!item.acceptedQuantity) errs[`acceptedQuantity_${idx}`] = "Required";
      if (!item.warehouseCode) errs[`warehouseCode_${idx}`] = "Required";
      
      if (item.productTrackingType === 1) { 
        if (!item.batches || item.batches.length === 0) {
          errs[`batches_${idx}`] = "At least one batch is required";
        } else {
          const batchErrors = [];
          item.batches.forEach((batch, batchIdx) => {
            if (!batch.batchNumber || batch.batchNumber.trim() === '') {
              batchErrors.push(`Batch ${batchIdx + 1}: Batch number is required`);
            }
            if (!batch.expiryDate) {
              batchErrors.push(`Batch ${batchIdx + 1}: Expiry date is required`);
            }
            if (!batch.quantity || parseFloat(batch.quantity) <= 0) {
              batchErrors.push(`Batch ${batchIdx + 1}: Quantity must be greater than 0`);
            }
          });
          
          if (batchErrors.length > 0) {
            errs[`batches_${idx}`] = batchErrors.join('; ');
          }
          
          const totalBatchQty = item.batches.reduce(
            (sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0
          );
          const acceptedQty = parseFloat(item.acceptedQuantity) || 0;
          
          if (Math.abs(totalBatchQty - acceptedQty) > 0.001) {
            errs[`batches_${idx}`] = (errs[`batches_${idx}`] ? errs[`batches_${idx}`] + '; ' : '') + 
              `Total batch quantity (${totalBatchQty}) must equal accepted quantity (${acceptedQty})`;
          }
        }
      }
      
      if (item.productTrackingType === 2) { 
        const expectedCount = parseInt(item.acceptedQuantity) || 0;
        if (!item.serialNumbers || item.serialNumbers.length !== expectedCount) {
          errs[`serialNumbers_${idx}`] = `All ${expectedCount} serial numbers are required`;
        }
      }
      
      if (!item.isFromPO && !item.unitPrice) errs[`unitPrice_${idx}`] = "Required";
      if (showDepartmentItemField && !item.department_id) errs[`department_id_${idx}`] = "Required";
    });

    // --- 3. GLOBAL UNIQUENESS CHECK (Serials & Batches) ---
    
    // Maps to track occurrences
    const serialOccurrences = {}; // Key: Serial (uppercase), Value: Array of row indices
    const batchOccurrences = {};  // Key: ProductCode_BatchNumber (uppercase), Value: Array of row indices

    items.forEach((item, idx) => {
      // Check Serials (Tracking Type 2)
      if (item.productTrackingType === 2 && Array.isArray(item.serialNumbers)) {
        item.serialNumbers.forEach(sn => {
          const cleanSN = sn.trim().toUpperCase();
          if (cleanSN) {
            if (!serialOccurrences[cleanSN]) serialOccurrences[cleanSN] = [];
            serialOccurrences[cleanSN].push(idx);
          }
        });
      }

      // Check Batches (Tracking Type 1)
      if (item.productTrackingType === 1 && Array.isArray(item.batches)) {
        item.batches.forEach(b => {
          const cleanBN = (b.batchNumber || "").trim().toUpperCase();
          const prodKey = (item.productCode || "").trim().toUpperCase();
          // Uniqueness constraint: Batch Number + Product Code
          const uniqueKey = `${prodKey}_${cleanBN}`; 

          if (cleanBN) {
            if (!batchOccurrences[uniqueKey]) batchOccurrences[uniqueKey] = [];
            batchOccurrences[uniqueKey].push(idx);
          }
        });
      }
    });

    // Apply Serial Errors
    Object.keys(serialOccurrences).forEach(serial => {
      const indices = serialOccurrences[serial];
      if (indices.length > 1) {
        // Mark all rows containing this duplicate serial
        indices.forEach(idx => {
          const currentErr = errs[`serialNumbers_${idx}`] || "";
          errs[`serialNumbers_${idx}`] = currentErr
            ? `${currentErr} Duplicate serial '${serial}' found in rows.`
            : `Duplicate serial '${serial}' found in rows.`;
        });
      }
    });

    // Apply Batch Errors
    Object.keys(batchOccurrences).forEach(key => {
      const indices = batchOccurrences[key];
      if (indices.length > 1) {
        // Extract batch number from the key "PROD123_BATCH001"
        const batchNumber = key.split('_').pop(); 
        // Get product name from the first affected item
        const prodName = items[indices[0]].productName || items[indices[0]].productCode; 

        indices.forEach(idx => {
          const currentErr = errs[`batches_${idx}`] || "";
          errs[`batches_${idx}`] = currentErr
            ? `${currentErr} Duplicate batch '${batchNumber}' for '${prodName}'.`
            : `Duplicate batch '${batchNumber}' for '${prodName}'.`;
        });
      }
    });

    setErrors(errs);
    return errs;
  };
  
  // Helper function to get remaining quantity
  const getRemainingQuantity = (item) => {
    // Simply return the remaining quantity from the item
    return item.remainingQuantity || "0";
  };
  
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // ADD THIS FUNCTION:
  const showToast = (message, type = 'error', title = 'Validation Error', duration = 4000) => {
    setToast({ open: true, message, type, title });

    setTimeout(() => {
      setToast(prev => ({ ...prev, open: false }));
    }, duration);
  };

    // Helper to parse response safely (handles JSON errors and non-JSON responses)
  const parseResponse = async (res) => {
    const contentType = res.headers.get("content-type");
    const textData = await res.text();
    
    // console.log("Response Content-Type:", contentType);
    // console.log("Response Text Data (first 200 chars):", textData.substring(0, 200));

    if (contentType && contentType.includes('application/json')) {
      try {
        return JSON.parse(textData);
      } catch (e) {
        // console.error("JSON Parse Error:", e);
        throw new Error("Server returned invalid JSON data.");
      }
    } else {
      // If it's not JSON (e.g., an error message wrapped in HTML), alert the user.
      // console.warn("Response was not JSON. Raw text:", textData);
      // alert(`Server Response (${res.status}): ${textData}`);
      showToast(`Server Response (${res.status}): ${textData}`, "error", "Server Error", 6000);
      return null; 
    }
  };
  
     const handleSubmit = async (e) => {
    e.preventDefault();
     // 1. Capture the errors object
    const errors = validate();
    
    // 2. If there are errors, show the Toast
    if (Object.keys(errors).length > 0) {
      // Get the first error message found to show in the toast
      const firstError = Object.values(errors)[0];
      
      showToast(
        firstError || "Please fix the errors in the form.", 
        "error", 
        "Validation Failed"
      );
      return; // Stop submission
    }
    
    setIsSubmitting(true);
    setLoading(true);
    try {
      const formData = new FormData();

      if (headerFileInputRef.current && headerFileInputRef.current.files[0]) {
        formData.append("headerAttachment", headerFileInputRef.current.files[0]);
      }

      // Create ONE single MRN object
      const mrnData = {
        MRNNumber: header.mrnNumber,
        MRNDate: header.mrnDate,
        VendorCode: header.vendorCode,
        ChallanNumber: header.challanNumber,
        ReceivedBy: header.receivedBy,
        VendorInvoiceNo : header.vendorInvoiceNo,
        InvoiceReceivedDate: header.invoiceReceivedDate || null,
        EWayBillNumber: header.eWayBillNumber,
        VehicleNumber: header.vehicleNumber,
        Transporter: header.transporter,
        GRNumber: header.grNumber,
        ProjectCode: header.projectCode, 
        DeliveryNoteNumber: header.deliveryNoteNumber,
        PendingBillNumber: header.pendingBillNumber,
        BillDate: header.billDate || null,
        Status: Number(header.status),
        Remarks: header.remarks,
        Action: mode === "edit" ? "Update" : "Create",
        InventoryFlag: header.inventoryFlag === "yes",
        BranchCode: header.branchCode,
        LocationCode: header.locationCode,
        updateInventory: header.updateInventory,
        ...(showDepartmentField && header.department_id ? { department_id: parseInt(header.department_id) } : {}),
        ...(mode === "edit" ? { updated_by: parseInt(currentUser.UserID) } : { created_by: parseInt(currentUser.UserID) }),
        
        
        // Map ALL items into this single object
        Items: items.map((item) => {
          
          // --- NEW LOGIC: Determine ProductTrackingType based on isLot and isSerial ---
          let productTrackingType = 0; // Default None
          
          // Find the master product to check boolean flags
          const masterProduct = productItems.find(p => p.ProductCode === item.productCode);
          
          if (masterProduct) {
            // Priority: Serial (2) > Batch (1) > None (0)
            if (masterProduct.isSerial === true) {
              productTrackingType = 2; // Serial
            } else if (masterProduct.isLot === true) {
              productTrackingType = 1; // Batch
            }
          } 
          
          // Fallback to existing state integer if master flags are not found
          if (productTrackingType === 0) {
            productTrackingType = item.productTrackingType || 0;
          }
          // -----------------------------------------------------------------------

          return {
            MRNItemID: item.mrnItemId || null,
            ItemCode: item.itemCode,
            ProductName: item.productName,
            ProductCode: item.productCode || null, 
            OrderedQty: parseFloat(item.orderedQuantity) || 0,
             POLineNumber: item.poLineNumber || null,
           
            ReceivedQty: parseFloat(item.receivedQuantity) || 0,
            ShortageQty: parseFloat(item.shortageQty) || 0, 
            UOM: item.uom,
            AcceptedQty: parseFloat(item.acceptedQuantity) || 0,
            RejectedQty: parseFloat(item.rejectedQuantity) || 0,
            BranchCode: item.branchCode || null,
            WarehouseCode: item.warehouseCode,
            ProjectCode: item.projectCode, 
            Attachment: item.attachment,
            AttachmentFileName: item.attachmentFileName,
            Remarks: item.remarks,
            MRNItemNumber: item.MRNItemNumber,
            MRNNumber: header.mrnNumber,
            tempId: item.tempId,
            ...(showDepartmentItemField && item.department_id ? { department_id: parseInt(item.department_id) } : {}),
            PurchaseOrderNumber: item.purchaseOrderNumber || null,
            
            UnitPrice: parseFloat(item.unitPrice) || 0,
            Discount: parseFloat(item.discount) || 0,
            TaxCode: item.taxCode || null,
            TaxValue: parseFloat(item.taxValue) || 0,
            Total: parseFloat(item.total) || 0,
            
            // SEND the calculated tracking type
            ProductTrackingType: productTrackingType,
            
            // Send Batches ONLY if type is Batch (1)
            Batches: productTrackingType === 1 ? 
              (item.batches || []).map(batch => ({
                BatchNumber: (batch.batchNumber || batch.BatchNumber || "").toUpperCase(),
                ExpiryDate: batch.expiryDate || batch.ExpiryDate || "",
                Quantity: parseFloat(batch.quantity || batch.Quantity || 0)
              })) : 
              [],
              
            // Send SerialNumbers ONLY if type is Serial (2)
            SerialNumbers: productTrackingType === 2 ? item.serialNumbers : [],
          };
        }),
      };

      formData.append("mrnData", JSON.stringify(mrnData));
      // console.log("mrn data ", JSON.stringify(mrnData));
      
      // Handle file attachments
      items.forEach((item, idx) => {
        if (fileInputRefs.current[idx] && fileInputRefs.current[idx].files[0]) {
          formData.append(
            `attachment_${item.tempId}`,
            fileInputRefs.current[idx].files[0]
          );
        }
      });
      
      const url =
        mode === "edit"
          ? `${API_URL}/mrn/${header.mrnDocumentId}`
          : `${API_URL}/mrn`;
      const method = mode === "edit" ? "PUT" : "POST";
      
      // console.log("Submitting to:", url, method);

      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });
      
      // console.log("Raw Response Status:", response.status);
      // console.log("Raw Response Headers:", response.headers);

      const result = await parseResponse(response);

        if (!response.ok) {
          // If status is not OK, the parseResponse helper handles the alert. We just need to throw to enter catch block.
          const errorMessage = (result && result.message) 
            ? result.message 
            : `Request failed with status ${response.status}`;

          throw new Error(errorMessage); ;
        }

      // If we got valid JSON data, proceed with success modal
      if (result) {
        
       
        // --- CHANGE THIS SECTION ---
        // 1. Call the parent's success handler
        if (onFormSuccess) {
          onFormSuccess(result, mode === "edit" ? 'Updated' : 'Created');
        }

        // 2. Close the form immediately
        onClose();
        // ----------------------------
    
      }

    } catch (err) {
      // console.error("Error saving material receipt note:", err);
      // alert(err.message || "Error saving material receipt note");
      showToast(err.message || "Error saving material receipt note", "error");
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

    // --- RESIZABLE HELPER COMPONENT ---
  const ResizableTh = ({ width, onResize, children, align = 'left' }) => (
    <th
      style={{ width: width, position: 'relative', userSelect: 'none' }}
      className={`p-2 bg-[#BDDDFC] text-black  text-m font-semibold ${
        align === 'center' ? 'text-center' : 'text-left'
      }`}
    >
      {children}
      <div
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize hover:bg-blue-400"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation(); // Prevent sorting or other events
          onResize(e.clientX);
        }}
      />
    </th>
  );

  // --- RESIZABLE HANDLERS ---
  const startResize = (columnKey, startX) => {
    resizingRef.current = { columnKey, startX, startWidth: columnWidths[columnKey] };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', stopResize);
  };

  const handleMouseMove = (e) => {
    if (!resizingRef.current) return;
    const { columnKey, startX, startWidth } = resizingRef.current;
    const delta = e.clientX - startX;
    const newWidth = Math.max(40, startWidth + delta); // Min width 40px

    setColumnWidths((prev) => ({
      ...prev,
      [columnKey]: newWidth,
    }));
  };

  const stopResize = () => {
    resizingRef.current = null;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', stopResize);
  };

  
 const getItemErrors = () => {
  const itemErrors = [];
  items.forEach((_, idx) => {
    if (errors[`receivedQuantity_${idx}`]) {
      itemErrors.push(`Item ${idx + 1}: ${errors[`receivedQuantity_${idx}`]}`);
    }
    if (errors[`acceptedQuantity_${idx}`]) {
      itemErrors.push(`Item ${idx + 1}: ${errors[`acceptedQuantity_${idx}`]}`);
    }
    if (errors[`warehouseCode_${idx}`]) {
      itemErrors.push(`Item ${idx + 1}: Warehouse is required`);
    }
    if (errors[`attachment_${idx}`]) {
      itemErrors.push(`Item ${idx + 1}: ${errors[`attachment_${idx}`]}`);
    }
    if (errors[`batchNumber_${idx}`]) {
      itemErrors.push(`Item ${idx + 1}: ${errors[`batchNumber_${idx}`]}`);
    }
    if (errors[`expiryDate_${idx}`]) {
      itemErrors.push(`Item ${idx + 1}: ${errors[`expiryDate_${idx}`]}`);
    }
    if (errors[`serialNumbers_${idx}`]) {
      itemErrors.push(`Item ${idx + 1}: ${errors[`serialNumbers_${idx}`]}`);
    }
    if (errors[`batches_${idx}`]) {
      itemErrors.push(`Item ${idx + 1}: ${errors[`batches_${idx}`]}`);
    }
  });
  return itemErrors;
};
  
  const shouldShowItemRows = items.length > 0;
  
  if (!isOpen) return null;
  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex justify-center transition-all duration-300 ease-in-out ${
          isOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
      >
        <style>
          {`
            .hide-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
            .hide-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .custom-scrollbar::-webkit-scrollbar {
              height: 8px;
            }
            .custom-scrollbar::-webkit-scrollbar-track {
              background: #f1f5f9;
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 10px;
            }
            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;

            }

            /* Chrome, Edge, Safari */
            input[type=number]::-webkit-outer-spin-button,
            input[type=number]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }

            /* Firefox */
            input[type=number] {
              -moz-appearance: textfield;
            }
              // ... existing styles like .hide-scrollbar, .custom-scrollbar ...

            @keyframes toastIn {
              from {
                opacity: 0;
                transform: translate(-50%, -16px) scale(0.98);
              }
              to {
                opacity: 1;
                transform: translate(-50%, 0) scale(1);
              }
            }

            @keyframes toastProgress {
              from { width: 100%; }
              to { width: 0%; }
            }

            .animate-toastIn {
              animation: toastIn 0.25s ease-out;
            }

            .animate-toastProgress {
              animation: toastProgress 4s linear forwards;
            }
               .resizable-table {
            table-layout: fixed;
            width: 100%;
            border-collapse: collapse;
          }
          
          .resizable-table th, 
          .resizable-table td {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
          
          /* Ensure inputs fill the cell width */
          .resizable-table input,
          .resizable-table select {
            width: 100%;
            min-width: 0; 
            box-sizing: border-box;
          }

          `}
        </style>
        <div
          className="absolute inset-0 bg-black bg-opacity-50"
          onClick={onClose}
        ></div>
        <div
          className={`bg-white shadow-2xl flex flex-col transition-transform transform-gpu duration-300 ease-in-out ${
            isOpen ? "translate-x-0" : "translate-x-full"
          } ${isMaximized ? "w-full h-full max-w-none m-0 rounded-none" : "w-full max-w-6xl h-full rounded-none"}`}
        >
          <div className="flex-shrink-0 p-8 pb-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-[#384959]">
                {mode === "edit"
                  ? "Edit Material Receipt Note"
                  : "Create Material Receipt Note"}
              </h2>
              <div className="flex gap-2">
                <button
                  className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
                  onClick={toggleMaximize}
                  title={isMaximized ? "Minimize" : "Maximize"}
                >
                  {isMaximized ? <MinimizeIcon size={24} /> : <MaximizeIcon size={24} />}
                </button>
                <button
                  className="text-[#A0AEC0] hover:text-[#384959] transition-colors"
                  onClick={onClose}
                  title="Close"
                >
                  <CloseIcon size={24} />
                </button>
              </div>
            </div>
          </div>
          
          {/* User validation warning */}
          {isOpen && !isUserValid && (
            <div className="mx-8 mb-4 bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {authError || 'User information not available. Please refresh the page and log in again.'}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex-grow overflow-y-auto hide-scrollbar px-8">
            <form onSubmit={handleSubmit} className="space-y-6 pb-6">
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-lg font-semibold text-[#384959] mb-4">
                  Material Receipt Note Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      MRN Number <span className='text-red-600'>*</span>
                    </label>
                    <input
                      name="mrnNumber"
                      value={header.mrnNumber}
                      onChange={handleHeaderChange}
                      disabled={true}
                      className="w-full border rounded-lg px-3 py-2 bg-[#F1F5F9] border-[#E2E8F0] uppercase"
                    />
                    {errors.mrnNumber && (
                      <span className="text-xs text-red-500">
                        {errors.mrnNumber}
                      </span>
                    )}
                  </div>
                  {/* Approval/BC Status - ADD THIS NEW FIELD */}
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-[#64748b]">Approval/BC Status</label>
              <div className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F1F5F9]">
                {!initialData ? (
                  <span className="text-xs font-medium text-gray-600 italic">Open</span>
                ) : (header.SendToBC === 1 || header.SendToBC === true) ? (
                  <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">
                    Data Successfully Sent to BC
                  </span>
                ) : (header.ValidateData === 1 || header.ValidateData === true) ? (
                  <span className="text-xs font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded border border-purple-200">
                    Validated (Ready to Send BC)
                  </span>
                ) : header.ApprovalStatus === 'PENDING' ? (
                  <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-1 rounded border border-amber-200">
                    Pending Approval (Level {header.CurrentLevel || 1})
                  </span>
                ) : header.ApprovalStatus === 'REJECTED' ? (
                  <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-1 rounded border border-red-200">
                    Rejected
                  </span>
                ) : header.ApprovalStatus === 'APPROVED' ? (
                  <span className="text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                    Approved (Ready to Validate)
                  </span>
                ) : (
                  <span className="text-xs text-gray-500 italic">Open</span>
                )}
              </div>
            </div>
                  {/* <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      MRN Date 
                    </label>
                    <input
                      type="date"
                      name="mrnDate"
                      value={header.mrnDate}
                      onChange={handleHeaderChange}
                      className={`w-full border rounded-lg px-3 py-2 ${
                        errors.mrnDate ? "border-red-400" : "border-[#E2E8F0]"
                      } focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
                    />
                    {errors.mrnDate && (
                      <span className="text-xs text-red-500">
                        {errors.mrnDate}
                      </span>
                    )}
                  </div> */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Supplier Name <span className='text-red-600'>*</span>
                    </label>
                   <SearchableDropdown
  options={dropdownData.vendors}
  value={header.vendorCode}
  onChange={(vendorNo) => {
    handleHeaderChange({
      target: { name: 'vendorCode', value: vendorNo }
    });
  }}
  getOptionLabel={(vendor) => `${vendor.vendorNo} - ${vendor.vendorName}`}
  getOptionValue={(vendor) => vendor.vendorNo}
  getOptionSecondaryLabel={(vendor) => vendor.vendorName}
  placeholder="Select Supplier"
  error={errors.vendorCode}
/>
                    {errors.vendorCode && (
                      <span className="text-xs text-red-500">
                        {errors.vendorCode}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    Location <span className='text-red-600'>*</span>
  </label>
  <SearchableDropdown
    options={dropdownData.locations}
    value={header.locationCode}
    onChange={(locationCode) => {
      handleHeaderChange({
        target: { name: 'locationCode', value: locationCode }
      });
    }}
    getOptionLabel={(loc) => `${loc.locationCode} - ${loc.locationName}`}
    getOptionValue={(loc) => loc.locationCode}
    getOptionSecondaryLabel={(loc) => loc.locationName}
    placeholder="Select Location"
    error={errors.locationCode}
  />
  {errors.locationCode && (
    <span className="text-xs text-red-500">{errors.locationCode}</span>
  )}
</div>
                  
{/* Vendor Details - Clean Card */}
{header.vendorCode && (
  <div className="mt-4 p-5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
    <h4 className="text-sm font-semibold text-[#384959] mb-3">Supplier Details</h4>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      <div>
        <span className="text-xs font-medium text-[#64748b]">Address</span>
        <p className="text-sm text-gray-800 mt-1">
          {dropdownData.vendors.find(v => v.vendorNo === header.vendorCode)?.address || 'N/A'}
        </p>
      </div>
      
      <div>
        <span className="text-xs font-medium text-[#64748b]">Phone</span>
        <p className="text-sm text-gray-800 mt-1">
          {dropdownData.vendors.find(v => v.vendorNo === header.vendorCode)?.phone || 'N/A'}
        </p>
      </div>
    </div>
  </div>
)}

{/* Location Details - Clean Card */}
{header.locationCode && (
  <div className="mt-4 p-5 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
    <h4 className="text-sm font-semibold text-[#384959] mb-3">Receipt Location Details</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <span className="text-xs font-medium text-[#64748b]">Address</span>
        <p className="text-sm text-gray-800 mt-1">
          {dropdownData.locations.find(l => l.locationCode === header.locationCode)?.address || 'N/A'}
        </p>
      </div>
      <div>
        <span className="text-xs font-medium text-[#64748b]">City</span>
        <p className="text-sm text-gray-800 mt-1">
          {dropdownData.locations.find(l => l.locationCode === header.locationCode)?.city || 'N/A'}
        </p>
      </div>
    </div>
  </div>
)}

                   <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Branch *
                    </label>
                    <SearchableDropdown
                    options={dropdownData.branches}
                    value={header.branchCode}
                    onChange={(branchCode) => {
                      handleHeaderChange({
                        target: { name: 'branchCode', value: branchCode }
                      });
                    }}
                    getOptionLabel={(branch) => `${branch.BranchCode} - ${branch.BranchName}`}
                    getOptionValue={(branch) => branch.BranchCode}
                    getOptionSecondaryLabel={(branch) => branch.BranchName}
                    placeholder="Select Branch"
                    error={errors.branchCode}
                    className="uppercase"
                  />
                    {errors.branchCode && (
                      <span className="text-xs text-red-500">
                        {errors.branchCode}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                  <label className="block text-sm font-medium text-[#64748b]">
                    Project Code <span className='text-red-600'>*</span>
                  </label>
                  <SearchableDropdown
                    options={dropdownData.projects}
                    value={header.projectCode}
                    onChange={(val) => setHeader({ ...header, projectCode: val })}
                    getOptionLabel={(proj) => `${proj.No} - ${proj.Description}`}
                    getOptionValue={(proj) => proj.No}
                    getOptionSecondaryLabel={(proj) => proj.Description}
                    placeholder="Select Project"
                    error={errors.projectCode}
                  />
                </div>

                   <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Received By <span className='text-red-600'>*</span>
                    </label>
                    <input
                      name="receivedBy"
                      value={header.receivedBy}
                      onChange={handleHeaderChange}
                      className={`w-full border rounded-lg px-3 py-2 ${
                        errors.receivedBy
                          ? "border-red-400"
                          : "border-[#E2E8F0]"
                      } focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
                    />
                    {errors.receivedBy && (
                      <span className="text-xs text-red-500">
                        {errors.receivedBy}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    Vendor Invoice No <span className='text-red-600'>*</span>
  </label>
  <input
    type="text"
    name="vendorInvoiceNo"
    value={header.vendorInvoiceNo || ""}
    onChange={handleHeaderChange}
    className={`w-full border rounded-lg px-3 py-2 ${
                        errors.vendorInvoiceNo
                          ? "border-red-400"
                          : "border-[#E2E8F0]"
                      } focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase`}
  />
  {errors.vendorInvoiceNo && (
                      <span className="text-xs text-red-500">
                        {errors.vendorInvoiceNo}
                      </span>
                    )}
</div>

<div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    Invoice Received Date <span className='text-red-600'>*</span>
  </label>
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      value={header.invoiceReceivedDate ? dayjs(header.invoiceReceivedDate) : null}
      onChange={(newValue) => {
        setHeader({
          ...header,
          invoiceReceivedDate: newValue ? newValue.format('YYYY-MM-DD') : ''
        });
      }}
      format="DD/MM/YYYY"
      slotProps={{
        textField: {
          size: 'small',
          className: 'w-full',
          error: !!errors.invoiceReceivedDate
        }
      }}
    />
  </LocalizationProvider>
  {errors.invoiceReceivedDate && (
    <span className="text-xs text-red-500">
      {errors.invoiceReceivedDate}
    </span>
  )}
</div>
 <div className="flex flex-col gap-2">
  <label className="block text-sm font-medium text-[#64748b]">
    Bill Date <span className='text-red-600'>*</span>
  </label>
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DatePicker
      value={header.billDate ? dayjs(header.billDate) : null}
      onChange={(newValue) => {
        setHeader({
          ...header,
          billDate: newValue ? newValue.format('YYYY-MM-DD') : ''
        });
      }}
      format="DD/MM/YYYY"
      slotProps={{
        textField: {
          size: 'small',
          className: 'w-full',
           error: !!errors.billDate
        }
      }}
    />
  </LocalizationProvider>
</div>
                 
                  
                  {/* Department Selection Field - Only shown if ShowOnHeader is true */}
                  {showDepartmentField && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-sm font-medium text-[#64748b]">
                        Department <span className='text-red-600'>*</span>
                      </label>
                     <SearchableDropdown
  options={departments}
  value={header.department_id}
  onChange={(department_id) => {
    handleHeaderChange({
      target: { name: 'department_id', value: department_id }
    });
  }}
  getOptionLabel={(dept) => `${dept.DepartmentCode} - ${dept.DepartmentName}`}
  getOptionValue={(dept) => dept.DepartmentID.toString()}
  getOptionSecondaryLabel={(dept) => dept.DepartmentName}
  placeholder={loadingDepartments ? 'Loading departments...' : 'Select Department'}
  error={errors.department_id}
  disabled={loadingDepartments}
  className="uppercase"
/>
                      {errors.department_id && (
                        <span className="text-xs text-red-500">
                          {errors.department_id}
                        </span>
                      )}
                    </div>
                  )}

                  

                  {/* EWay Bill Number */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      E-Way Bill Number
                    </label>
                    <input
                      type="text"
                      name="eWayBillNumber"
                      value={header.eWayBillNumber}
                      onChange={handleHeaderChange}
                      className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>

                  {/* Vehicle Number */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Vehicle Number
                    </label>
                    <input
                      type="text"
                      name="vehicleNumber"
                      value={header.vehicleNumber}
                      onChange={handleHeaderChange}
                      className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>

                  {/* Transporter */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Transporter
                    </label>
                    <input
                      type="text"
                      name="transporter"
                      value={header.transporter}
                      onChange={handleHeaderChange}
                      className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>

                  {/* GR Number */}
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      GR Number
                    </label>
                    <input
                      type="text"
                      name="grNumber"
                      value={header.grNumber}
                      onChange={handleHeaderChange}
                      className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>
                  
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Delivery Note Number
                    </label>
                    <input
                      type="text"
                      name="deliveryNoteNumber"
                      value={header.deliveryNoteNumber}
                      onChange={handleHeaderChange}
                      className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Challan Number
                    </label>
                    <input
                      type="text"
                      name="challanNumber"
                      value={header.challanNumber}
                      onChange={handleHeaderChange}
                      className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>
                  {/* <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Pending Bill Number
                    </label>
                    <input
                      type="text"
                      name="pendingBillNumber"
                      value={header.pendingBillNumber}
                      onChange={handleHeaderChange}
                      className="w-full border rounded-lg px-3 py-2 border-[#E2E8F0] focus:outline-none focus:ring-2 focus:ring-[#BDDDFC]"
                    />
                  </div> */}
                
                  
                  {/* <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b] uppercase">
                      Status
                    </label>
                    <select
                      name="status"
                      value={Number(header.status)}
                      onChange={handleHeaderChange}
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    >
                      {STATUS_OPTIONS.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </div> */}
 

            
                  {/* <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Add to Inventory *
                    </label>
                    <select
                      name="inventoryFlag"
                      value={header.inventoryFlag}
                      onChange={handleHeaderChange}
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    >
                      {INVENTORY_FLAG_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Select "Yes" to add items to inventory, "No" to skip inventory update</p>
                  </div> */}

                {/* Header Attachment Field */}
                <div className="flex flex-col gap-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                        Attachment <span className='text-red-600'>*</span>
                    </label>
                    <div className="flex flex-col items-start">
                        <input
                            type="file"
                            ref={headerFileInputRef}
                            onChange={handleHeaderFileChange}
                            className="hidden"
                            id="header-file-upload"
                        />
                                        <label
                            htmlFor="header-file-upload"
                            className={`cursor-pointer flex items-center ${
                                errors.headerAttachment 
                                    ? 'text-red-600 hover:text-red-800' 
                                    : 'text-blue-600 hover:text-blue-800'
                            }`}
                        >
                            <FaPaperclip className="mr-1" />
                            {header.attachmentFileName ? (
                                <span className="text-xs truncate max-w-[150px]">
                                    {header.attachmentFileName}
                                </span>
                            ) : (
                                <span className="text-xs">Attach File</span>
                            )}
                        </label>
                        {errors.headerAttachment && (
                            <span className="text-xs text-red-500 mt-1">
                                {errors.headerAttachment}
                            </span>
                        )}
                                        
                        {header.attachmentFileName && (
                            <div className="flex mt-1 gap-2">
                                {mode === "edit" && header.mrnNumber && (
                                    <button
                                        type="button"
                                        onClick={handleDownloadHeaderAttachment}
                                        className="text-green-600 hover:text-green-800 text-xs"
                                        title="Download"
                                    >
                                        <FaDownload size={12} />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleRemoveHeaderAttachment}
                                    className="text-red-600 hover:text-red-800 text-xs"
                                    title="Remove"
                                >
                                    <FaTrash size={12} />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <label className="block text-sm font-medium text-[#64748b]">
                      Remarks
                    </label>
                    <textarea
                      name="remarks"
                      value={header.remarks}
                      onChange={handleHeaderChange}
                      rows={3}
                      className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#BDDDFC] uppercase"
                    />
                  </div>
                </div>
              </div>
              <div className="bg-[#F9FAFB] rounded-xl p-6 border border-[#E2E8F0]">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#384959]">
                    Material Receipt Items
                  </h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="px-4 py-2 bg-[#6A89A7] text-white rounded-lg hover:bg-[#88BDF2] flex items-center transition-colors"
                      onClick={addItem}
                      disabled={!header.vendorCode}
                    >
                      <FiLink className="mr-1" /> Link to PO
                    </button>
                    {/* <button
                      type="button"
                      className="px-4 py-2 bg-[#4CAF50] text-white rounded-lg hover:bg-[#45a049] flex items-center transition-colors"
                      onClick={addManualItem}
                    >
                      <FiPlus className="mr-1" /> Add Item
                    </button> */}
                  </div>
                </div>
                {errors.items && (
                  <div className="text-red-500 text-sm mb-2">
                    {errors.items}
                  </div>
                )}
                <div className="overflow-x-auto custom-scrollbar">
                  <table className="resizable-table border border-[#E2E8F0]">
                    <thead>
                      <tr className="bg-[#BDDDFC] text-[#384959]">
                        <ResizableTh width={columnWidths.rowNum} onResize={(x) => startResize('rowNum', x)} align="center">#</ResizableTh>
                        <ResizableTh width={columnWidths.productCode} onResize={(x) => startResize('productCode', x)}>Product Code</ResizableTh>
                         <ResizableTh width={columnWidths.productName} onResize={(x) => startResize('productName', x)}>Product Name</ResizableTh>
                         <ResizableTh width={columnWidths.uom} onResize={(x) => startResize('uom', x)}>UOM</ResizableTh>
                        <ResizableTh width={columnWidths.rate} onResize={(x) => startResize('rate', x)}>Rate</ResizableTh>
                        <ResizableTh width={columnWidths.balanceQty} onResize={(x) => startResize('balanceQty', x)}>Balance Qty</ResizableTh>

                        <ResizableTh width={columnWidths.receivedQty} onResize={(x) => startResize('receivedQty', x)}>Received Quantity</ResizableTh>
                       <ResizableTh width={columnWidths.shortExcess} onResize={(x) => startResize('shortExcess', x)}>Short/Excess Qty</ResizableTh>
                        
                        {/* <th className="p-2">Accepted Quantity</th>
                        <th className="p-2">Rejected Quantity</th> */}
                        
                        {/* <th className="p-2">Discount</th> */}
                        {/* <th className="p-2">Tax Code</th>
                        <th className="p-2">Tax Value</th> */}
                        {/* <th className="p-2">Total</th> */}
                       
                        <ResizableTh width={columnWidths.location} onResize={(x) => startResize('location', x)}>Location</ResizableTh>
                        <ResizableTh width={columnWidths.branch} onResize={(x) => startResize('branch', x)}>Branch</ResizableTh>
                        <ResizableTh width={columnWidths.projectCode} onResize={(x) => startResize('projectCode', x)}>Project Code</ResizableTh>
        
                        {/* Department column - Only shown if ShowOnItem is true */}
                        {showDepartmentItemField && <th className="p-2">Department</th>}
                        {/* Tracking columns */}
                        <ResizableTh width={columnWidths.trackingType} onResize={(x) => startResize('trackingType', x)}>Tracking Type</ResizableTh>
                        <ResizableTh width={columnWidths.batchSerial} onResize={(x) => startResize('batchSerial', x)}>Batch/Serial Info</ResizableTh>
                        <ResizableTh width={columnWidths.poNumber} onResize={(x) => startResize('poNumber', x)}>PO Number</ResizableTh>
                        <ResizableTh width={columnWidths.poLine} onResize={(x) => startResize('poLine', x)}>PO Line</ResizableTh>
                        {/* <ResizableTh width={columnWidths.attachment} onResize={(x) => startResize('attachment', x)}>Attachment <span className='text-red-600'>*</span></ResizableTh> */}
        
                        {/* <th className="p-2">Item Code</th> */}
                       
                        
                        <ResizableTh width={columnWidths.remarks} onResize={(x) => startResize('remarks', x)}>Remarks</ResizableTh>
                        <ResizableTh width={columnWidths.action} onResize={(x) => startResize('action', x)} align="center">Action</ResizableTh>
      
                      </tr>
                    </thead>
                    <tbody>
                      {shouldShowItemRows ? (
                        items.map((item, idx) => (
                          <tr key={idx} className="border-t border-[#E2E8F0]">
                            <td className="p-2 text-center" style={{ width: columnWidths.rowNum }}>{idx + 1}</td>
                            <td className="p-2" style={{ width: columnWidths.productCode }}>
                              <input
                                name="productCode"
                                value={item.productCode || ""}
                                onChange={(e) => handleItemChange(idx, e)}
                                disabled={item.isFromPO}
                                className={`w-24 border rounded-lg px-2 py-1 ${
                                  item.isFromPO ? "bg-[#F1F5F9]" : ""
                                } border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                                readOnly
                              />
                            </td>
                            <td className="p-2" style={{ width: columnWidths.productName }}>
                            <SearchableDropdown
                              options={productItems}
                              value={item.productCode}
                              onChange={(productCode) => {
                                const newItems = [...items];
                            // Inside the table's SearchableDropdown onChange
                            const selectedProduct = productItems.find(p => p.ProductCode === productCode);
                            if (selectedProduct) {
                              newItems[idx].productName = selectedProduct.ProductName || "";
                              newItems[idx].productCode = selectedProduct.ProductCode || "";
                              newItems[idx].uom = selectedProduct.UOMName || "";
                              
                              // FIX: Check multiple possible property names (camelCase, PascalCase, or Allow...)
                              const trackingType = selectedProduct.productTrackingType ?? selectedProduct.ProductTrackingType ?? selectedProduct.AllowProductTracking ?? 0;
                              newItems[idx].productTrackingType = trackingType;

                              // FIX: Use the variable trackingType for the condition checks
                              if (trackingType === 1) { // Batch
                                setCurrentBatchItem(idx);
                                setShowBatchModal(true);
                              } else if (trackingType === 2) { // Serial
                                newItems[idx].serialNumbers = [];
                              } else { // None
                                newItems[idx].batchNumber = "";
                                newItems[idx].expiryDate = "";
                                newItems[idx].serialNumbers = [];
                              }
                            }
                                setItems(newItems);
                              }}
                              getOptionLabel={(product) => product.ProductName}
                              getOptionValue={(product) => product.ProductCode}
                              getOptionSecondaryLabel={(product) => product.ProductCode}
                              placeholder="Select Product"
                              width="w-full"
                              error={errors[`productName_${idx}`]}
                              disabled={item.isFromPO}
                            />

                              {errors[`productName_${idx}`] && (
                                <span className="text-xs text-red-500">*</span>
                              )}
                            </td>
                            
                              <td className="p-2" style={{ width: columnWidths.uom }}>
                              <input
                                name="uom"
                                value={item.uom || ""}
                                onChange={(e) => handleItemChange(idx, e)}
                                disabled={item.isFromPO}
                                className={`w-full border rounded-lg px-2 py-1 ${
                                  item.isFromPO ? "bg-[#F1F5F9]" : ""
                                } ${errors[`uom_${idx}`] ? "border-red-400" : "border-[#E2E8F0]"} focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                                title={item.isFromPO ? "Auto-populated from PO" : "Select UOM"}
                              />
                              {errors[`uom_${idx}`] && (
                                <span className="text-xs text-red-500">*</span>
                              )}
                            </td>
                             <td className="p-2" style={{ width: columnWidths.rate }}>
                              {item.isFromPO ? 
                                (item.unitPrice || "N/A") : 
                                <input
                                  type="number"
                                  name="unitPrice"
                                  value={item.unitPrice}
                                  onChange={(e) => handleItemChange(idx, e)}
                                  className={`w-full border rounded-lg px-2 py-1 ${
                                    errors[`unitPrice_${idx}`] ? "border-red-400" : "border-[#E2E8F0]"
                                  } focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]`}
                                  placeholder="0.00"
                                  step="1"
                                />
                              }
                              {errors[`unitPrice_${idx}`] && (
                                <span className="text-xs text-red-500">*</span>
                              )}
                            </td>
                              <td className="p-2" style={{ width: columnWidths.balanceQty }}>
                              <input
                                name="remainingQuantity"
                                type="number"
                                value={item.remainingQuantity || "0"}
                                inputMode="decimal"  step="0.000001"
                                readOnly
                                className="w-full border rounded-lg px-2 py-1 border-[#E2E8F0] bg-[#F1F5F9] text-gray-600 uppercase"
                                title="Remaining quantity from PO"
                              />
                            </td>
                            <td className="p-2" style={{ width: columnWidths.receivedQty }}>
                              <input
                                name="receivedQuantity"
                                type="number"
                                min="0"
                                inputMode="decimal"  step="0.000001"
                                value={item.receivedQuantity}
                                onChange={(e) => handleItemChange(idx, e)}
                                className={`w-full border rounded-lg px-2 py-1 ${
                                  errors[`receivedQuantity_${idx}`]
                                    ? "border-red-400"
                                    : "border-[#E2E8F0]"
                                } focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                              />
                            </td>
                            <td className="p-2" style={{ width: columnWidths.shortExcess }}>
                              <input
                                name="shortageQty"
                                type="number"
                                min="0"
                                inputMode="decimal"  step="0.000001"
                                value={item.shortageQty || ""}
                                onChange={(e) => handleItemChange(idx, e)}
                                className="w-full border rounded-lg px-2 py-1 border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                              />
                            </td>
                           
                          
                            {/* <td className="p-2">
                              <input
                                name="acceptedQuantity"
                                type="number"
                                min="0"
                                value={item.acceptedQuantity}
                                onChange={(e) => handleItemChange(idx, e)}
                                readOnly
                                className={`w-16 border rounded-lg px-2 py-1 ${
                                  errors[`acceptedQuantity_${idx}`]
                                    ? "border-red-400"
                                    : "border-[#E2E8F0]"
                                } focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                              />
                            </td> */}
                            {/* <td className="p-2">
                              <input
                                name="rejectedQuantity"
                                type="number"
                                min="0"
                                value={item.rejectedQuantity}
                                onChange={(e) => handleItemChange(idx, e)}
                                className="w-16 border rounded-lg px-2 py-1 border-[#E2E8F0] bg-[#F1F5F9] uppercase"
                                readOnly
                              />
                            </td> */}
                            {/* Unit Price */}
                           
                            {/* Discount */}
                            {/* <td className="p-2">
                              {item.isFromPO ? 
                                (item.discount || "N/A") : 
                                <input
                                  type="number"
                                  name="discount"
                                  value={item.discount}
                                  onChange={(e) => handleItemChange(idx, e)}
                                  className="w-16 border rounded-lg px-2 py-1 border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                                  placeholder="0.00"
                                  step="1"
                                />
                              }
                            </td> */}
                            {/* Tax Code */}
                            {/* <td className="p-2">
                              {item.isFromPO ? 
                                (item.taxCode || "N/A") : 
                                <SearchableDropdown
  options={taxCodes}
  value={item.taxCode}
  onChange={(taxCode) => {
    const newItems = [...items];
    newItems[idx].taxCode = taxCode;
    
    // Recalculate tax and total when tax code changes
    const totals = calculateItemTotal(newItems[idx]);
    newItems[idx].taxValue = totals.taxValue;
    newItems[idx].total = totals.total;
    
    setItems(newItems);
  }}
  getOptionLabel={(tax) => tax.TaxCode}
  getOptionValue={(tax) => tax.TaxCode}
  getOptionSecondaryLabel={(tax) => `${tax.Rate}%`}
  placeholder="Select Tax"
  width="w-20"
  disabled={item.isFromPO}
/>
                              }
                            </td> */}
                            {/* Tax Value */}
                            {/* <td className="p-2">
                              {item.isFromPO ? 
                                (item.taxValue || "N/A") : 
                                <input
                                  type="number"
                                  name="taxValue"
                                  value={item.taxValue}
                                  readOnly
                                  className="w-16 border rounded-lg px-2 py-1 border-[#E2E8F0] bg-[#F1F5F9]"
                                />
                              }
                            </td> */}
                            {/* Total */}
                            {/* <td className="p-2">
                              {item.isFromPO ? 
                                (item.total || "N/A") : 
                                <input
                                  type="number"
                                  name="total"
                                  value={item.total}
                                  readOnly
                                  className="w-16 border rounded-lg px-2 py-1 border-[#E2E8F0] bg-[#F1F5F9]"
                                />
                              }
                            </td> */}
                            <td className="p-2" style={{ width: columnWidths.location }}>
                              <SearchableDropdown
                                options={dropdownData.locations}
                                value={item.warehouseCode}
                                onChange={(warehouseCode) => {
                                  const newItems = [...items];
                                  newItems[idx].warehouseCode = warehouseCode;
                                  setItems(newItems);
                                }}
                                // FIX: Handle both camelCase and PascalCase for LocationCode
                                getOptionLabel={(location) => location.LocationName || location.locationName || location.name || ''}
                                getOptionValue={(location) => location.LocationCode || location.locationCode || ''} 
                                getOptionSecondaryLabel={(location) => location.LocationCode || location.locationCode || ''}
                                placeholder="Select Warehouse"
                                width="w-full"
                                error={errors[`warehouseCode_${idx}`]}
                                disabled={item.isFromPO}
                              />
                              {errors[`warehouseCode_${idx}`] && (
                                <span className="text-xs text-red-500">*</span>
                              )}
                            </td>

                            <td className="p-2" style={{ width: columnWidths.branch }}>
                              <input
                                name="branchCode"
                                value={item.branchCode || ""}
                                className="w-full border rounded-lg px-2 py-1 border-[#E2E8F0] bg-[#F1F5F9] uppercase"
                                readOnly
                                placeholder={item.isFromPO ? "" : "N/A"}
                              />
                            </td>

                            <td className="p-2" style={{ width: columnWidths.projectCode }}>
                              <input
                                name="projectCode"
                                value={item.projectCode || ""}
                                className="w-full border rounded-lg px-2 py-1 border-[#E2E8F0] bg-[#F1F5F9] uppercase"
                                readOnly // Usually derived from PO, so readOnly is safer
                              />
                            </td>
                                                        {/* Department cell - Only shown if ShowOnItem is true */}
                            {showDepartmentItemField && (
                              <td className="p-2">
                                <SearchableDropdown
                                  options={departments}
                                  value={item.department_id}
                                  onChange={(department_id) => {
                                    const newItems = [...items];
                                    newItems[idx].department_id = department_id;
                                    setItems(newItems);
                                  }}
                                  getOptionLabel={(dept) => dept.DepartmentCode}
                                  getOptionValue={(dept) => dept.DepartmentID.toString()}
                                  getOptionSecondaryLabel={(dept) => dept.DepartmentName}
                                  placeholder="Select"
                                  width="w-24"
                                  error={errors[`department_id_${idx}`]}
                                  disabled={item.isFromPO || loadingDepartments}
                                  className="uppercase"
                                />
                                {errors[`department_id_${idx}`] && (
                                  <span className="text-xs text-red-500">*</span>
                                )}
                              </td>
                            )}
                            {/* Tracking Type */}
                                                    {/* Tracking Type */}
                          <td className="p-2" style={{ width: columnWidths.trackingType }}>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              item.productTrackingType === 0 ? 'bg-gray-100 text-gray-800' :
                              item.productTrackingType === 1 ? 'bg-blue-100 text-blue-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {item.productTrackingType === 0 ? 'None' :
                              item.productTrackingType === 1 ? 'Batch' : 'Serial'}
                            </span>
                          </td>
                          {/* Batch/Serial Info */}
                          <td className="p-2" style={{ width: columnWidths.batchSerial }}>
                            {item.productTrackingType === 1 && (
                              <div>
                                <div className="text-xs font-medium">
                                  {item.batches?.length || 0} batch{item.batches?.length !== 1 ? 'es' : ''}
                                </div>
                                {/* <div className="text-xs text-gray-500">
                                  Total qty: {item.batches?.reduce((sum, batch) => sum + (parseFloat(batch.quantity) || 0), 0).toFixed(6) || '0'}
                                </div> */}
                                {/* Display batch details with expiry dates */}
                                {/* {item.batches && item.batches.length > 0 && (
                                  <div className="mt-1 text-xs max-w-xs">
                                    {item.batches.slice(0, 2).map((batch, idx) => (
                                      <div key={idx} className="flex items-start mb-1">
                                        <span className="font-medium">{batch.batchNumber}</span>
                                        {batch.expiryDate && (
                                          <span className="ml-2 text-gray-500">
                                            (Exp: {new Date(batch.expiryDate).toLocaleDateString()})
                                          </span>
                                        )}
                                        <span className="ml-2 text-gray-600">
                                          Qty: {batch.quantity}
                                        </span>
                                      </div>
                                    ))}
                                    {item.batches.length > 2 && (
                                      <div className="text-gray-500">
                                        +{item.batches.length - 2} more
                                      </div>
                                    )}
                                  </div>
                                )} */}
                                <button
                                  type="button"
                                  className="text-blue-600 hover:text-blue-800 text-xs mt-1"
                                  onClick={() => {
                                    setCurrentBatchItem(idx);
                                    setShowBatchModal(true);
                                  }}
                                >
                                  Edit Batches
                                </button>
                              </div>
                            )}
                            {item.productTrackingType === 2 && (
                              <div>
                                <div className="text-xs font-medium">
                                  {item.serialNumbers?.length || 0} of {item.acceptedQuantity} serials
                                </div>
                                <button
                                  type="button"
                                  className="text-blue-600 hover:text-blue-800 text-xs mt-1"
                                  onClick={() => {
                                    setCurrentSerialItem(idx);
                                    setShowSerialModal(true);
                                  }}
                                >
                                  Edit Serials
                                </button>
                              </div>
                            )}
                            {item.productTrackingType === 0 && (
                              <span className="text-gray-400 text-sm">No tracking</span>
                            )}
                          </td>
                            <td className="p-2" style={{ width: columnWidths.poNumber }}>
                              <input
                                name="purchaseOrderNumber"
                                value={item.purchaseOrderNumber || ""}
                                onChange={(e) => handleItemChange(idx, e)}
                                className="w-full border rounded-lg px-2 py-1 border-[#E2E8F0] bg-[#F1F5F9] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase"
                                readOnly
                                placeholder={item.isFromPO ? "" : "N/A"}
                              />
                            </td>
                             <td className="p-2 text-center" style={{ width: columnWidths.poLine }}>
                            <span className="text-s  text-black bg-gray-100 px-2 py-1 rounded">
                              {item.poLineNumber || "-"}
                            </span>
                          </td>
                            {/* <td className="p-2">
                              <input
                                name="itemCode"
                                value={item.itemCode || ""}
                                onChange={(e) => handleItemChange(idx, e)}
                                disabled={item.isFromPO}
                                className={`w-24 border rounded-lg px-2 py-1 ${
                                  item.isFromPO ? "bg-[#F1F5F9]" : ""
                                } border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC] uppercase`}
                                placeholder={item.isFromPO ? "" : "N/A"}
                              />
                            </td> */}
                            {/* <td className="p-2" style={{ width: columnWidths.attachment }}>
                              <div className="flex flex-col items-center">
                                <input
                                  type="file"
                                  ref={(el) =>
                                    (fileInputRefs.current[idx] = el)
                                  }
                                  onChange={(e) => handleFileChange(idx, e)}
                                  className="hidden"
                                  id={`file-upload-${idx}`}
                                />
                                <label
                                  htmlFor={`file-upload-${idx}`}
                                  className={`cursor-pointer flex items-center ${
                                  errors[`attachment_${idx}`] 
                                    ? 'text-red-600 hover:text-red-800' // Highlight red if error
                                    : 'text-blue-600 hover:text-blue-800'
                                }`}
                                >
                                  <FaPaperclip className="mr-1" />
                                  {item.attachmentFileName ? (
                                    <span className="text-xs truncate max-w-[100px]">
                                      {item.attachmentFileName}
                                    </span>
                                  ) : (
                                    <span className="text-xs">Attach File</span>
                                  )}
                                </label>
                                {errors[`attachment_${idx}`] && (
                              <span className="text-xs text-red-500 mt-1">
                                Required
                              </span>
                            )}
                                {item.attachmentFileName && (
                                  <div className="flex mt-1">
                                    {mode === "edit" && item.MRNItemNumber && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          handleDownloadAttachment(idx)
                                        }
                                        className="text-green-600 hover:text-green-800 mr-2"
                                        title="Download"
                                      >
                                        <FaDownload size={12} />
                                      </button>
                                    )}
                                    <button
                                      type="button"
                                      onClick={() =>
                                        handleRemoveAttachment(idx)
                                      }
                                      className="text-red-600 hover:text-red-800"
                                      title="Remove"
                                    >
                                      <FaTrash size={12} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td> */}
                            <td className="p-2" style={{ width: columnWidths.remarks }}>
                              <input
                                name="remarks"
                                type="text"
                                value={item.remarks}
                                onChange={(e) => handleItemChange(idx, e)}
                                className="w-24 border rounded-lg px-2 py-1 border-[#E2E8F0] focus:outline-none focus:ring-1 focus:ring-[#BDDDFC]"
                              />
                            </td>
                            <td className="p-2 text-center" style={{ width: columnWidths.action }}>
                              <button
                                type="button"
                                className="text-red-500 hover:text-red-700 transition-colors"
                                onClick={() => removeItem(idx)}
                              >
                                <TrashIcon />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={showDepartmentItemField ? 22 : 20}
                            className="text-center text-gray-500 py-4"
                          >
                            No items added yet. Click "Add Item" to add items to
                            this MRN.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {getItemErrors().length > 0 && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <h4 className="text-red-700 font-medium mb-2">Please fix the following errors:</h4>
                    <ul className="text-red-600 text-sm space-y-1">
                      {getItemErrors().map((error, index) => (
                        <li key={index} className="flex items-start">
                          <span className="mr-2">•</span>
                          <span>{error}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {shouldShowItemRows && (
                  <div className="mt-6 bg-white p-4 rounded-lg border border-[#E2E8F0]">
                    <h4 className="text-md font-semibold text-[#384959] mb-4">
                      Total Summary
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">
                          Total Items
                        </span>
                        <span className="text-base font-semibold">
                          {summary.totalItems}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">
                          Total Received Quantity
                        </span>
                        <span className="text-base font-semibold">
                          {summary.totalReceivedQuantity}
                        </span>
                      </div>
                      {/* <div className="flex flex-col">
                        <span className="text-sm text-gray-500">
                          Total Accepted Quantity
                        </span>
                        <span className="text-base font-semibold">
                          {summary.totalAcceptedQuantity}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">
                          Total Rejected Quantity
                        </span>
                        <span className="text-base font-semibold text-red-600">
                          {summary.totalRejectedQuantity}
                        </span>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">
                          Total Amount
                        </span>
                        <span className="text-base font-semibold text-green-600">
                          {summary.totalAmount}
                        </span>
                      </div> */}
                    </div>
                  </div>
                )}
              </div>
            </form>
          </div>
          <div className="flex-shrink-0 p-8 pt-0">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                className="px-6 py-3 rounded-lg text-[#64748b] font-semibold transition-all duration-300 ease-in-out hover:bg-[#F1F5F9]"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-3 rounded-lg text-white font-semibold shadow-lg transition-all duration-300 ease-in-out transform hover:scale-105 bg-[#6A89A7] hover:bg-[#88BDF2] disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || isSubmitting || !isUserValid}
                onClick={handleSubmit}
              >
                {loading || isSubmitting
                  ? "Saving..."
                  : mode === "edit"
                  ? "Update MRN"
                  : "Create MRN"}
              </button>
            </div>
          </div>
        </div>
      </div>
      <POItemsSelectionModal
  isOpen={showPOItemsModal}
  onClose={() => setShowPOItemsModal(false)}
  vendorCode={header.vendorCode}
  locationCode={header.locationCode}
   branchCode={header.branchCode}
    projectCode={header.projectCode}
  onItemsSelected={handlePOItemsSelected}
    existingItems={items} // <-- ADD THIS LINE
/>


      <ProductMultiSelectModal
        open={showMultiSelectModal}
        onClose={() => setShowMultiSelectModal(false)}
        products={productItems}
        onConfirm={handleMultiSelectProducts}
        onAutofill={autofillSelectedProducts}
      />

    
      <BatchTableModal
        isOpen={showBatchModal}
        onClose={() => {
          setShowBatchModal(false);
          setCurrentBatchItem(null);
        }}
        onSave={handleSaveBatches}
        initialBatches={currentBatchItem !== null ? items[currentBatchItem]?.batches || [] : []}
        productName={currentBatchItem !== null ? items[currentBatchItem]?.productName : ""}
        acceptedQuantity={currentBatchItem !== null ? parseFloat(items[currentBatchItem]?.acceptedQuantity) || 0 : 0}
      />

            
      <SerialModal
        isOpen={showSerialModal}
        onClose={() => {
          setShowSerialModal(false);
          setCurrentSerialItem(null);
        }}
        onSave={handleSaveSerial}
        count={currentSerialItem !== null ? parseInt(items[currentSerialItem]?.acceptedQuantity) || 0 : 0}
        initialSerialNumbers={currentSerialItem !== null ? items[currentSerialItem]?.serialNumbers : []}
        productName={currentSerialItem !== null ? items[currentSerialItem]?.productName : ""}
        locationCode={currentSerialItem !== null ? items[currentSerialItem]?.warehouseCode : ""}
      />

      {/* --- ADD THIS TOAST BLOCK HERE --- */}
      {toast.open && (
        <div className="fixed top-5 left-1/2 -translate-x-1/2 z-[99999] animate-toastIn">
          <div
            className={`
              relative w-[420px] rounded-2xl shadow-2xl border backdrop-blur-md overflow-hidden
              ${toast.type === 'error' && 'bg-red-50/90 border-red-200'}
              ${toast.type === 'warning' && 'bg-amber-50/90 border-amber-200'}
              ${toast.type === 'success' && 'bg-green-50/90 border-green-200'}
            `}
          >
            {/* Content */}
            <div className="flex gap-4 px-5 py-4">
              {/* Icon */}
              <div className="pt-1">
                {toast.type === 'error' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-red-100">
                    <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                )}

                {toast.type === 'warning' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-amber-100">
                    <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                  </div>
                )}
                
                {toast.type === 'success' && (
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-green-100">
                    <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                )}
              </div>

              {/* Text */}
              <div className="flex-1">
                <div className="font-semibold text-sm text-gray-800 mb-1">
                  {toast.title}
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed">
                  {toast.message}
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div
              className={`
                absolute bottom-0 left-0 h-[3px] animate-toastProgress
                ${toast.type === 'error' && 'bg-red-500'}
                ${toast.type === 'warning' && 'bg-amber-500'}
                ${toast.type === 'success' && 'bg-green-500'}
              `}
            />
          </div>
        </div>
      )};

    </>
  );
};

export default MaterialReceiptNoteFormModal;